import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useForm,
  Controller,
  useFieldArray,
  useWatch,
  FieldArrayWithId,
  Control,
  UseFormSetValue,
  UseFieldArrayReturn,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Chip,
  Switch,
  Divider,
  FormControlLabel,
  IconButton,
  Tooltip,
  InputAdornment,
  Tab,
  Tabs,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  Print as PrintIcon,
  FileOpen as FileIcon,
  ContentCopy,
  Paid,
} from "@mui/icons-material";
import {
  ApproveIcon,
  RejectIcon,
} from "../../components/common/NucleusSymbolsAndIcons";
import { PercentageField } from "../../components/common/CustomFields";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  useTSQPurchOrder,
  useTSMCreatePurchOrder,
  useTSMPostProcessPurchOrder,
  useTSMUpdatePurchOrder,
  useTSMReceivePurchOrder,
  useTSMGenerateRecDocs,
  useTSMUpdatePurchOrderApproval,
  useTSMCompletePO,
  useTSMVoidPO,
  useTSMStatusUpdatePO,
} from "../../hooks/useTSPurchaseOrder";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import { useTSQItemList } from "../../hooks/useTSItems";
import { useTSQUOMUnitList } from "../../hooks/useTSUnits";
import { useTSQLocationList } from "../../hooks/useTSLocations";
import { useTSQInvStatusList } from "../../hooks/useTSInvStatus";
import { useTSQCompanyList, useTSQPeopleList } from "../../hooks/useTSEntities";
import { useTSQAddressList } from "../../hooks/useTSAddresses";
import { PAYMENT_TERMS, PaymentTermType } from "../../types/common/termTypes";
import {
  PurchOrderFull,
  PurchOrderCreate,
  PurchOrderUpdate,
  PurchOrderStatusUpdate,
  POLineCreate,
  POLineUpdate,
  POLineReceipt,
  POReceiving,
  POReceiptResponse,
} from "../../types/purchOrderTypes";
import { InvLotReceipt } from "../../types/lotTypes";
import {
  TypedSelectOption,
  YupTypedSelectOption,
} from "../../types/common/selectOptions";
import {
  AddressFieldOld,
  AddressField,
} from "../../components/common/AddressViewEdit";
import {
  NumericDisplayField,
  NumericEntryField,
} from "../../components/common/CustomFields";
import AuditHistoryViewer from "../../components/common/AuditHistory";
import stringUtils from "../../utils/stringUtils";
import OrderStatusActions from "../../components/common/OrderStatusActionsPanel";
import { useWorldLocationData } from "../../contexts/WorldLocationContextProvider";
import statusUtils from "../../utils/statusUtils";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import { IdAsUUID } from "../../types/common/commonTypes";
import { TableObject } from "../../types/common/baseTableTypes";
import useFormParams, { BASE_FORM_MODES } from "../../hooks/useFormHooks";
import PageHeaderComponent from "../../components/common/PageHeader";
import { CustomButtonProps } from "../../components/common/_CommonProps";
import SplitButton, {
  SplitButtonProps,
} from "../../components/common/SplitButton";

const PURCH_ORDER_FORM_MODES = [...BASE_FORM_MODES, "receive"];
type PurchOrderFormMode = (typeof PURCH_ORDER_FORM_MODES)[number];

interface PurchOrderFormParams {
  id: string | null;
}

type LocationOption = TypedSelectOption<"location">;
type EmployeeOption = TypedSelectOption<"employee">;
type VendorOption = TypedSelectOption<
  "vendor",
  { bill_addr: string | null; terms: string }
>;
const CentOpt: VendorOption = {
  label: "Centese",
  type: "vendor",
  value: {
    id: "centese",
    bill_addr: "centeseaddr",
    terms: "",
  },
};

type PaymentTermOption = TypedSelectOption<"payment_term">;

const paymentTermOptions: PaymentTermOption[] = (() => {
  return PAYMENT_TERMS.filter((a) => a !== "UNKNOWN")
    .sort((a, b) => a.localeCompare(b))
    .map((val) => ({
      label: stringUtils.capFirstLetter(val.replace(/[_]/g, " ")),
      type: "payment_term",
      value: { id: val },
    }));
})();

type ItemOption = TypedSelectOption<
  "item",
  {
    number: string;
    name: string;
    revision: string;
    uomTypeId: string;
    item_type: string;
    expires: boolean;
    purch_uom: string;
  }
>;
type UOMOption = TypedSelectOption<
  "unit",
  { symbol: string; conversion: number; typeId: string }
>;
type StatusOption = TypedSelectOption<"inv_status">;

interface PrevRecData {
  id: IdAsUUID;
  number: string;
  unit: string;
  batch: string;
  exp_date: Date | null;
  rec_date: Date;
  rec_revision: string;
  rec_quantity: number;
  rec_by: string;
  rel_date: Date | null;
  rel_revision: string | null;
  rel_quantity: number | null;
  rel_by: string;
}

interface NewRecData {
  quantity: number;
  revision_received: string;
  uom_unit: UOMOption;
  supp_code: string | null;
  expiration_date: string | null;
}
type newRecKey = keyof NewRecData;

interface POLineData {
  rec_id: IdAsUUID | null;
  revision_ordered: string;
  item_name_override: string | null;
  expected_date: Date | null;
  quantity_ordered: number;
  quantity_received: number;
  unit_cost: number;
  notes: string | null;
  item: ItemOption | null;
  uom_unit: UOMOption | null;
  is_delete: boolean;
  requires_receipt: boolean;
  mark_complete: boolean;
  line_completed: boolean;
  is_taxed: boolean;
  new_recs: NewRecData[];
}

interface POFormData {
  number: string;
  order_date: Date;
  location: LocationOption | null;
  is_blanket: boolean;
  expected_date: Date | null;
  vendor: VendorOption | null;
  billing_addr: TableObject<"orderaddress">;
  is_dropship: boolean;
  shipping_addr: TableObject<"orderaddress">;
  order_terms: PaymentTermOption | null;
  employee: EmployeeOption | null;
  tax_rate: number;
  tax_amount: number;
  ship_cost: number;
  notes: string | null;
  line_items: POLineData[];
}

// Validation schema
const purchOrderSchema = yup.object({
  number: yup.string().nullable().defined(),
  order_date: yup.date().required("Order Date is Required"),
  location: YupTypedSelectOption.required("PO Location is Required"),
  is_blanket: yup.boolean().required(),
  expected_date: yup
    .date()
    .nullable()
    .test({
      name: "main-expected-date",
      test: function (value, context) {
        if (!context.parent.is_blanket && !value) {
          return context.createError({
            path: "expected_date",
            message: "Standard PO Expected Date is Required",
          });
        }
        return true;
      },
    }),
  vendor: YupTypedSelectOption.required("Vendor is Required"),
  billing_addr: yup
    .object({
      attn: yup
        .string()
        .nullable()
        .test({
          name: "billing-addr-attn",
          test: function (value, context) {
            if (!context.parent.addressee && !value) {
              return context.createError({
                path: "billing_addr.attn",
                message: "Either Attention or Addressee must be Provided",
              });
            }
            return true;
          },
        }),
      addressee: yup
        .string()
        .nullable()
        .test({
          name: "billing-addr-addressee",
          test: function (value, context) {
            if (!context.parent.attn && !value) {
              return context.createError({
                path: "billing_addr.addressee",
                message: "Either Attention or Addressee must be Provided",
              });
            }
            return true;
          },
        }),
      line_1: yup.string().required("Address Line 1 is Required"),
      line_2: yup.string().nullable(),
      line_3: yup.string().nullable(),
      city: yup.string().required("City is Required"),
      state: yup.string().required("State is Required"),
      country: yup.string().required("Country is Required"),
      zip: yup.string().required("Zip Code is Required"),
      phone: yup.string().nullable(),
      email: yup.string().nullable(),
    })
    .required("Billing Address is Required"),
  is_dropship: yup.boolean().required(),
  shipping_addr: yup
    .object({
      attn: yup
        .string()
        .nullable()
        .test({
          name: "shipping-addr-attn",
          test: function (value, context) {
            if (!context.parent.addressee && !value) {
              return context.createError({
                path: "shipping_addr.attn",
                message: "Either Attention or Addressee must be Provided",
              });
            }
            return true;
          },
        }),
      addressee: yup
        .string()
        .nullable()
        .test({
          name: "shipping-addr-addressee",
          test: function (value, context) {
            if (!context.parent.attn && !value) {
              return context.createError({
                path: "shipping_addr.addressee",
                message: "Either Attention or Addressee must be Provided",
              });
            }
            return true;
          },
        }),
      line_1: yup.string().required("Address Line 1 is Required"),
      line_2: yup.string().nullable(),
      line_3: yup.string().nullable(),
      city: yup.string().required("City is Required"),
      state: yup.string().required("State is Required"),
      country: yup.string().required("Country is Required"),
      zip: yup.string().required("Zip Code is Required"),
      phone: yup.string().nullable(),
      email: yup.string().nullable(),
    })
    .required("Shipping Address is Required"),
  order_terms: YupTypedSelectOption.required("Order Terms are Required"),
  employee: YupTypedSelectOption.required("PO Employee is Required"),
  tax_rate: yup
    .number()
    .required("Either Tax Rate or Tax Amount are required."),
  tax_amount: yup
    .number()
    .required("Either Tax Rate or Tax Amount are required."),
  ship_cost: yup.number().min(0, "Ship Cost is required, can be $0.00."),
  notes: yup.string().nullable().defined(),
  line_items: yup
    .array()
    .min(1, "At least one line item is required.")
    .of(
      yup.object({
        rec_id: yup.string().nullable().defined(),
        revision_ordered: yup
          .string()
          .required("Revision Ordered is required."),
        item_name_override: yup
          .string()
          .nullable()
          .defined()
          .test({
            name: "item-name-override",
            test: (value, context) => {
              const parentLine = context.parent;
              if (parentLine.item?.label === "PO Expense Item" && !value) {
                return context.createError({
                  message:
                    "Item Name Override is Required for PO Expense Items",
                });
              }
              return true;
            },
          }),
        expected_date: yup
          .date()
          .nullable()
          .test({
            name: "line-expected-date",
            test: function (value, context) {
              const rootObj = this.from ? this.from[1]?.value : undefined;
              if (rootObj?.is_blanket && !value) {
                return context.createError({
                  message:
                    "Line Level Expected Date is Required for Blanket POs",
                });
              }
              return true;
            },
          }),
        quantity_ordered: yup
          .number()
          .moreThan(0, "Quantity must be > 0.")
          .required(),
        quantity_received: yup.number().required(),
        unit_cost: yup.number().min(0, "Unit Cost must be >= 0.").required(),
        notes: yup.string().nullable().defined(),
        item: YupTypedSelectOption.required("Item is required."),
        uom_unit: YupTypedSelectOption.required("Unit of Measure is required."),
        is_delete: yup.boolean().required(),
        requires_receipt: yup.boolean().required(),
        mark_complete: yup.boolean().required(),
        line_completed: yup.boolean().required(),
        is_taxed: yup.boolean().required(),
        new_recs: yup
          .array()
          .of(
            yup
              .object({
                quantity: yup
                  .number()
                  .moreThan(0, "Receipt Quantity must be > 0.")
                  .required("Receipt Quantity is required."),
                revision_received: yup
                  .string()
                  .required("Received Revision is required."),
                uom_unit: YupTypedSelectOption.required(
                  "Received Unit of Measure is required.",
                ),
                supp_code: yup.string().nullable().defined(),
                expiration_date: yup
                  .string()
                  .nullable()
                  .test({
                    name: "newrec-expiration",
                    test: (value, context) => {
                      const parentLine = context.from
                        ? context.from[1].value
                        : null;
                      if (!parentLine) {
                        return context.createError({
                          message:
                            "Unable to access parent line for validation, please notify IT.",
                        });
                      }
                      if (parentLine.item.value.expires && !value) {
                        return context.createError({
                          message:
                            "Expiration Date is required for items that Expire",
                        });
                      }
                      return true;
                    },
                  }),
              })
              .required(),
          )
          .required(),
      }),
    ),
});

interface POPrintData {
  number: string;
  orderDate: string;
  expectedDate: string | null;
  isBlanket: boolean;
  shipMethod: string;
  notes: string | null;
  pricing: {
    sub: string;
    tax: string;
    taxRate: string;
    ship: string;
    total: string;
  };
  terms: string;
  vendor: {
    name: string;
    code: string;
    billing: {
      name: string;
      addressee: string;
      attn: string;
      line1: string;
      line2?: string;
      line3?: string;
      city: string;
      state: string;
      country: string;
      zip: string;
    };
  };
  isDropship: boolean;
  shipping: {
    addressee: string;
    attn: string;
    line1: string;
    line2?: string;
    line3?: string;
    city: string;
    state: string;
    country: string;
    zip: string;
  };
  items: {
    number: string;
    name: string;
    revision: string;
    quantity: string;
    notes: string;
    uom: string;
    rate: string;
    total: string;
    expDate: string | null;
  }[];
}
const generatePurchaseOrderHTML = (data: POPrintData) => {
  return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <link
      href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;700&display=swap"
      rel="stylesheet"
    />
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Purchase Order ${data.number}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: Arial, sans-serif;
            line-height: 1.4;
            color: #333;
            background: white;
            padding: 20px;
        }

        .container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding-bottom: 40px;
        }

        .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 30px;
            border-bottom: 2px solid #ddd;
            padding-bottom: 20px;
        }

        .company-info {
            display: flex;
            align-items: center;
        }

        .logo {
            color: #008675;
            padding: 10px 15px;
            font-size: 48px;
            font-weight: bold;
            font-family: Montserrat;
            margin-right: 20px;
        }

        .company-details {
            font-size: 12px;
            line-height: 1.3;
        }

        .document-info {
            text-align: right;
        }

        .document-title {
            font-size: 32px;
            font-weight: bold;
            margin-bottom: 8px;
        }

        .document-number {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 4px;
        }

        .addresses {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
            margin-bottom: 25px;
        }

        .address-section h3 {
            font-weight: bold;
            margin-bottom: 8px;
            font-size: 14px;
        }

        .address-section {
            font-size: 12px;
            line-height: 1.4;
        }

        .details-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 25px;
            font-size: 12px;
        }

        .details-table th,
        .details-table td {
            border: 1px solid #666;
            padding: 8px;
            text-align: center;
        }

        .details-table th {
            background-color: #f0f0f0;
            font-weight: bold;
        }

        .order-details {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 20px;
            margin-bottom: 25px;
            font-size: 12px;
        }

        .order-details > div {
            text-align: center;
            justify-content: space-between;
        }

        .order-details strong {
            display: block;
            font-weight: bold;
            margin-bottom: 4px;
        }

        .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 25px;
            font-size: 12px;
        }

        .items-table th,
        .items-table td {
            border: 1px solid #666;
            padding: 8px;
            text-align: left;
        }

        .items-table th {
            background-color: #f0f0f0;
            font-weight: bold;
        }

        .items-table .quantity,
        .items-table .unit-price,
        .items-table .extended-price {
            text-align: right;
        }

        .items-table .revision,
        .items-table .uom {
            text-align: left;
        }

        .items-table .revision,
        .items-table .quantity,
        .items-table .uom {
            width: 10%;
        }
        ${data.isBlanket ? ".items-table .itemexpected {width: 25%}" : ""}

        .item-code {
            font-weight: 600;
        }

        .item-description {
            font-size: 11px;
            color: #666;
        }

        .totals {
            margin-left: auto;
            width: 250px;
        }

        .totals table {
            width: 100%;
            font-size: 12px;
        }

        .totals td {
            padding: 6px 12px;
            background-color: #e8e8e8;
        }

        .totals .label {
            text-align: right;
            font-weight: bold;
        }

        .totals .amount {
            text-align: right;
        }

        .totals .total-row .amount {
            font-weight: bold;
        }

        .footer {
          position: fixed;
          bottom: 0;
          left: 0;
          width: 100%;
          text-align: center;
        }

        .footer p {
            text-align: center;
            font-size: 8px;
        }

        .barcode {
            text-align: center;
        }

        .barcode-graphic {
            font-family: monospace;
            font-size: 10px;
            background: black;
            color: white;
            padding: 4px 8px;
            display: inline-block;
            letter-spacing: 1px;
        }

        .barcode-text {
            font-size: 10px;
            margin-top: 4px;
        }

        .page-info {
            font-size: 10px;
            color: #666;
        }

        .print-controls {
            position: fixed;
            top: 10px;
            right: 10px;
            background: white;
            padding: 10px;
            border: 1px solid #ddd;
            border-radius: 4px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            z-index: 1000;
        }

        .print-controls button {
            margin: 0 5px;
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
        }

        .print-btn {
            background: #008675;
            color: white;
        }

        .print-btn:hover {
            background: #006b5a;
        }

        .mode-btn {
            background: #4CAF50;
            color: white;
        }

        .mode-btn:hover {
            background: #45a049;
        }

        .close-btn {
            background: #666;
            color: white;
        }

        .close-btn:hover {
            background: #555;
        }

        /* Print styles */
        @media print {
            .print-controls {
                display: none !important;
            }

            body {
                padding: 0;
            }

            @page {
                margin: 0.5in;
                size: letter;
            }
        }
    </style>
</head>
<body>
    <div class="print-controls">
        <button class="print-btn" onclick="window.print()">🖨️ Print</button>
    </div>

    <div class="container">
        <div class="header">
            <div class="company-info">
                <div class="logo">CENTESE</div>
                <div class="company-details">
                    <div>Centese, Inc.</div>
                    <div>4156 S. 52nd Street</div>
                    <div>Omaha, NE 68117</div>
                    <div>US</div>
                    <div>(888)220-0040</div>
                </div>
            </div>

            <div class="document-info">
                <div class="document-title">Purchase Order</div>
                <div class="document-number">${data.number}</div>
                <div>${data.orderDate}</div>
            </div>
        </div>

        <div class="addresses">
            <div class="address-section">
                <h3>Vendor Billing Address</h3>
                <div><strong>${data.vendor.name}</strong></div>
                <div>${data.vendor.billing.addressee}</div>
                <div>${data.vendor.billing.line1}</div>
                <div style="display:${
                  data.vendor.billing.line2 ? "inherit" : "none"
                }">${data.vendor.billing.line2}</div>
                <div style="display:${
                  data.vendor.billing.line3 ? "inherit" : "none"
                }">${data.vendor.billing.line3}</div>
                <div>${data.vendor.billing.city} ${data.vendor.billing.state} ${
                  data.vendor.billing.zip
                }</div>
                <div>${data.vendor.billing.country}</div>
            </div>
            <div class="address-section">
                <h3>${data.isDropship ? "DropShip" : "Ship"} To Address</h3>
                <div><strong>${data.shipping.addressee}</strong></div>
                <div>${data.shipping.attn}</div>
                <div>${data.shipping.line1}</div>
                <div style="display:${
                  data.shipping.line2 ? "inherit" : "none"
                }">${data.shipping.line2}</div>
                <div style="display:${
                  data.shipping.line3 ? "inherit" : "none"
                }">${data.shipping.line3}</div>
                <div>${data.shipping.city} ${data.shipping.state} ${
                  data.shipping.zip
                }</div>
                <div>${data.shipping.country}</div>
            </div>
            ${
              data.notes
                ? `<div class="address-section">
              <h3>Notes:</h3>
              <div><p>${data.notes}</p></div>
              </div>`
                : ``
            }
        </div>

        <table class="details-table">
            <thead>
                <tr>
                    <th>Expected Receipt Date</th>
                    <th>Terms</th>
                    <th>Shipping Method</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>${data.isBlanket ? "Blanket PO. See lines for expected dates." : data.expectedDate || "N/A"}</td>
                    <td>${data.terms}</td>
                    <td>${data.shipMethod}</td>
                </tr>
            </tbody>
        </table>

        <table class="items-table">
            <thead>
                <tr>
                    <th>Item</th>
                    <th class="revision">Revision</th>
                    ${data.isBlanket ? '<th class="itemexpected">Expected Date</th>' : ""}
                    <th class="quantity">Quantity</th>
                    <th class="uom">UoM</th>
                    <th class="unit-price">Unit Price</th>
                    <th class="extended-price">Extended Price</th>
                </tr>
            </thead>
            <tbody>
                ${data.items
                  .map(
                    (item) => `
                    <tr>
                        <td>
                            <div class="item-code">${item.number}</div>
                            <div class="item-description">${item.name}</div>
                            <div class="item-description">${item.notes}</div>
                        </td>
                        <td class="revision">${item.revision}</td>
                        ${data.isBlanket ? `<td class="itemexpected">${item.expDate}</td>` : ""}
                        <td class="quantity">${item.quantity}</td>
                        <td class="uom">${item.uom}</td>
                        <td class="unit-price">$${item.rate}</td>
                        <td class="extended-price">$${item.total}</td>
                    </tr>
                `,
                  )
                  .join("")}
            </tbody>
        </table>

        <div class="totals">
            <table>
                <tr>
                    <td class="label">Subtotal</td>
                    <td class="amount">$${data.pricing.sub}</td>
                </tr>
                <tr>
                    <td class="label">Tax (${data.pricing.taxRate}%)</td>
                    <td class="amount">$${data.pricing.tax}</td>
                </tr>
                <tr>
                    <td class="label">S&H</td>
                    <td class="amount">${data.pricing.ship}</td>
                </tr>
                <tr class="total-row">
                    <td class="label">Total</td>
                    <td class="amount">$${data.pricing.total}</td>
                </tr>
            </table>
        </div>
        <div class="footer">
            <p>By accepting this purchase order, the supplier agrees to notify Centese in writing of any changes to the product or service before implementation.</p>
            <p>Centese reserves the right to approve or reject such changes.</p>
        </div>
    </div>
</body>
</html>`;
};

const ReceiptDialog: React.FC<{
  open: any;
  control: Control<POFormData, any, POFormData>;
  onClose: () => void;
  index: number;
  uomOpts: UOMOption[];
  orderedUnit: UOMOption;
  neededQuant: number;
}> = ({
  open,
  control,
  onClose,
  index,
  uomOpts = [],
  orderedUnit,
  neededQuant,
}) => {
  const [currRecData, setCurrRecData] = useState<{
    quantity: number | null;
    revision_received: string | null;
    uom_unit: UOMOption | null;
    supp_code: string | null;
    expiration_date: string | null;
  }>({
    quantity: 0,
    revision_received: "",
    uom_unit: null,
    supp_code: "",
    expiration_date: null,
  });

  const {
    fields: recFields,
    append: recAppend,
    remove: recRemove,
    replace: recFieldsReplace,
  } = useFieldArray({
    control,
    name: `line_items.${index}.new_recs`,
  });

  const [error, setError] = useState<string | null>(null);
  const [quantPendingRec, setQuantPendingRec] = useState<number>(0);

  const canAdd = useMemo(() => {
    return (
      currRecData.quantity &&
      currRecData.quantity > 0 &&
      !!currRecData.revision_received &&
      !!currRecData.uom_unit
    );
  }, [currRecData]);

  const quantRemaining = useMemo(() => {
    return Math.max(neededQuant - quantPendingRec, 0);
  }, [quantPendingRec, neededQuant]);

  const addLine = () => {
    recAppend(currRecData as NewRecData);
    setCurrRecData({
      quantity: 0,
      revision_received: null,
      uom_unit: null,
      supp_code: null,
      expiration_date: null,
    });
  };

  const handleAdd = () => {
    if (canAdd) {
      addLine();
    }
  };

  const handleRemove = (ind: number) => {
    recRemove(ind);
  };

  const handleRegChange = (field: newRecKey) => (event: any) => {
    setCurrRecData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleQuantChange = (newValue: number | null) => {
    setCurrRecData((prev) => ({
      ...prev,
      quantity: newValue || 0,
    }));
  };

  const handleOptChange = (
    field: newRecKey,
    newValue: LocationOption | StatusOption | UOMOption | null,
  ) => {
    setCurrRecData((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };

  const handleDateChange = (newDate: Date | null) => {
    setCurrRecData((prev) => ({
      ...prev,
      expiration_date: newDate ? newDate.toISOString() : null,
    }));
  };

  useEffect(() => {
    if (open) {
      let temp = 0;
      for (let f of recFields) {
        const val: NewRecData = f as any;
        temp += val.quantity;
      }
      setQuantPendingRec(temp);
    } else {
      setQuantPendingRec(0);
    }
  }, [open, recFields]);

  const handleClose = (event: any, reason: string | null) => {
    if (reason === "backdropClick") {
      console.log("DIALOG CLOSE VIA BACKDROP CLICK");
    } else {
      console.log("DIALOG CLOSE VIA OTHER METHOD", event, reason);
    }
    setCurrRecData({
      quantity: 0,
      revision_received: "",
      uom_unit: null,
      supp_code: "",
      expiration_date: null,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Receipt</DialogTitle>
      <DialogContent>
        <Stack direction="column" width="100%" gap={2} p={5}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography variant="body2">Quantity Ordered:</Typography>
            <Typography variant="body2">{`${neededQuant}x ${
              orderedUnit?.label || ""
            }`}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography variant="body2">Quantity Pending Receipt:</Typography>
            <Typography variant="body2">{`${quantPendingRec}x ${
              orderedUnit?.label || ""
            }`}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography variant="body2">
              Quantity Remaining To Receive:
            </Typography>
            <Typography variant="body2">{`${quantRemaining}x ${
              orderedUnit?.label || ""
            }`}</Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "auto auto auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <Autocomplete
              sx={{ gridColumn: 1, gridRow: 1 }}
              fullWidth
              value={currRecData.uom_unit}
              options={uomOpts}
              onChange={(event, newValue: UOMOption | null) => {
                handleOptChange("uom_unit", newValue);
              }}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value.id === value?.value.id
              }
              renderInput={(params) => (
                <TextField {...params} size="small" label="UOM" />
              )}
            />
            <NumericEntryField
              value={currRecData.quantity || 0}
              onChange={handleQuantChange}
              min={0}
              decimalPlaces={4}
              label="Quantity"
              isCurrency={false}
              disabled={!currRecData.uom_unit}
              helperText={
                !currRecData.uom_unit
                  ? "Select a UOM before entering quantity"
                  : ""
              }
              textFieldProps={{
                size: "small",
                sx: { gridColumn: 2, gridRow: 1 },
              }}
            />
            <TextField
              sx={{ gridColumn: 1, gridRow: 2 }}
              fullWidth
              size="small"
              value={currRecData.revision_received || ""}
              onChange={handleRegChange("revision_received")}
              label="Revision Received"
            />
            <TextField
              sx={{ gridColumn: 2, gridRow: 2 }}
              fullWidth
              size="small"
              value={currRecData.supp_code || ""}
              onChange={handleRegChange("supp_code")}
              label="Batch Code"
            />
            <DatePicker
              sx={{ gridColumn: 1, gridRow: 3, width: "100%" }}
              label="Expiration Date"
              value={
                currRecData.expiration_date
                  ? new Date(currRecData.expiration_date)
                  : null
              }
              onChange={(date) => handleDateChange(date)}
            />
            <Button
              sx={{ gridColumn: 2, gridRow: 3 }}
              variant="outlined"
              color="primary"
              onClick={handleAdd}
              disabled={!canAdd}
              startIcon={<AddIcon />}
            >
              Confirm
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="5%">Row #</TableCell>
                  <TableCell width="auto">Revision</TableCell>
                  <TableCell width="auto">Quantity/Unit</TableCell>
                  <TableCell width="auto">Expiration/Batch Code</TableCell>
                  <TableCell width="20%">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recFields.map((newRec, newRecInd) => {
                  const value: NewRecData = newRec as any;
                  return (
                    <TableRow key={newRec.id}>
                      <TableCell>
                        <Typography variant="body1">
                          #{newRecInd + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {value.revision_received}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {`${value.quantity}x ${value.uom_unit.label}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack
                          sx={{
                            direction: "column",
                            gap: 1,
                          }}
                        >
                          <Typography variant="body1">
                            {`Expiration: ${
                              value.expiration_date !== ""
                                ? value.expiration_date
                                : "N/A"
                            }`}
                          </Typography>
                          <Typography variant="body1">
                            {`Batch Code: ${value.supp_code || "N/A"}`}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            handleRemove(newRecInd);
                          }}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={(e) => handleClose(e, "Close Button Press")}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const POLine: React.FC<{
  index: number;
  fieldData: FieldArrayWithId<POFormData, "line_items", "id">;
  control: Control<POFormData, any, POFormData>;
  setValue: any;
  allItems: ItemOption[];
  allUoms: UOMOption[];
  formMode: string;
  thisLineError: any;
  remove: (index: number) => void;
  onCopy: (index: number | null) => void;
  isBlanket: boolean;
}> = ({
  index,
  fieldData,
  control,
  setValue,
  allItems = [],
  allUoms = [],
  formMode,
  thisLineError,
  remove,
  onCopy,
  isBlanket,
}) => {
  const [filteredUOMOpts, setFilteredUOMOpts] = useState<UOMOption[]>(
    allUoms.filter(
      (uomOpt) =>
        uomOpt.value.typeId ===
        (fieldData.item ? fieldData.item.value.uomTypeId : null),
    ),
  );
  const [isDelete, setIsDelete] = useState<boolean>(fieldData.is_delete);
  const [dialogOpen, setReceiveDialogOpen] = useState<boolean>(false);

  const handleReceiveDialogOpen = () => {
    setReceiveDialogOpen(true);
  };

  const handleReceiveDialogClose = () => {
    setReceiveDialogOpen(false);
  };

  const handleRemoveLine = () => {
    if (fieldData.rec_id === null) {
      remove(index);
    } else {
      setValue(`line_items.${index}.is_delete`, !isDelete);
      setIsDelete(!isDelete);
    }
  };

  const handleItemChange = (newValue: ItemOption | null) => {
    // setSelectedItemUOMType(newValue ? newValue.value.uomTypeId : null);
    setFilteredUOMOpts(
      allUoms.filter(
        (uomOpt) =>
          uomOpt.value.typeId === (newValue ? newValue.value.uomTypeId : null),
      ),
    );
    // Reset UOM selection when item changes
    const uomOpt =
      allUoms.find((u) => u.value.id === newValue?.value.purch_uom) || null;
    setValue(`line_items.${index}.uom_unit`, uomOpt);
    setValue(
      `line_items.${index}.revision_ordered`,
      newValue ? newValue.value.revision : "",
    );
  };

  const [prevRecsOpen, setPrevRecsOpen] = useState<boolean>(false);
  const [prevRecPagination, setPrevRecPagination] = useState({
    page: 0,
    size: 25,
  });

  const handlePrevRecPageChange = (event: unknown, newPage: number) => {
    setPrevRecPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePrevRecRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPrevRecPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const {
    data: prevRecData,
    isLoading: prevRecsLoading,
    isFetching: prevRecsFetching,
    error: prevRecsError,
  } = useTSQDynamicList(
    {
      page: prevRecPagination.page,
      page_size: prevRecPagination.size,
      primary: "inventory_movements",
      alt_name: "mov",
      primary_fields: ["id"],
      joins: [
        {
          table: "inventory_positions",
          alt_name: "pos",
          fields: ["id"],
          condition: {
            remote_table: "inventory_movements",
            alt_name: "mov",
            remote_field: "to_position_id",
            local_field: "id",
          },
          outer: false,
        },
        {
          table: "inventory_lots",
          alt_name: "lot",
          fields: [
            "id",
            "lot_number",
            "supplier_lot_batch_code",
            "expiration_date",
            "received_date",
            "initial_revision",
            "quantity_received",
            "released_date",
            "released_revision",
            "quantity_released",
          ],
          condition: {
            remote_table: "inventory_positions",
            alt_name: "pos",
            remote_field: "inventory_lot_id",
            local_field: "id",
          },
          outer: false,
        },
        {
          table: "uom_units",
          alt_name: "unit",
          fields: ["unit_symbol"],
          condition: {
            remote_table: "inventory_lots",
            alt_name: "lot",
            remote_field: "received_uom_unit_id",
            local_field: "id",
          },
          outer: false,
        },
        {
          table: "employees",
          alt_name: "recby",
          fields: ["first_name", "last_name"],
          condition: {
            remote_table: "inventory_lots",
            alt_name: "lot",
            remote_field: "received_by_id",
            local_field: "id",
          },
          outer: true,
        },
        {
          table: "employees",
          alt_name: "relby",
          fields: ["first_name", "last_name"],
          condition: {
            remote_table: "inventory_lots",
            alt_name: "lot",
            remote_field: "released_by_id",
            local_field: "id",
          },
          outer: true,
        },
      ],
      sorts: [
        {
          table: "inventory_movements",
          alt_name: "mov",
          field: "movement_date",
          asc: true,
        },
      ],
      filters: [
        [
          {
            table: "inventory_movements",
            alt_name: "mov",
            field: "associated_id",
            operator: "==",
            value: fieldData.rec_id,
            is_not: false,
            inclusive_end: false,
            inclusive_start: false,
            case_insensitive: false,
          },
        ],
      ],
      subqueries: [],
    },
    prevRecsOpen && !!fieldData.rec_id,
  );

  const [prevRecs, prevRecCount]: [
    prevRecs: PrevRecData[],
    prevRecCount: number,
  ] = useMemo(() => {
    const prevRecs: PrevRecData[] = [];
    if (prevRecData?.items) {
      prevRecData.items.forEach((val) => {
        prevRecs.push({
          id: val["lot.id"],
          number: val["lot.lot_number"],
          unit: val["unit.unit_symbol"],
          batch: val["lot.supplier_lot_batch_code"] || "N/A",
          exp_date: val["lot.expiration_date"]
            ? new Date(val["lot.expiration_date"])
            : null,
          rec_date: new Date(val["lot.received_date"]),
          rec_revision: val["lot.initial_revision"],
          rec_quantity: parseFloat(val["lot.quantity_received"]),
          rec_by: val["recby.first_name"]
            ? `${val["recby.first_name"]} ${val["recby.last_name"]}`
            : "UNKNOWN",
          rel_date: val["lot.released_date"]
            ? new Date(val["lot.released_date"])
            : null,
          rel_revision: val["lot.released_revision"],
          rel_quantity: val["lot.quantity_released"]
            ? parseFloat(val["lot.quantity_released"])
            : null,
          rel_by: val["relby.first_name"]
            ? `${val["relby.first_name"]} ${val["relby.last_name"]}`
            : "UNKNOWN",
        });
      });
    }
    return [prevRecs, prevRecData?.total || 0];
  }, [prevRecData]);

  return (
    <>
      <TableRow>
        <TableCell>
          <Typography variant="body2" sx={{ textWrap: "nowrap" }}>
            {`#${index + 1}`}
          </Typography>
        </TableCell>
        <TableCell>
          <Stack spacing={1}>
            <Controller
              name={`line_items.${index}.item`}
              control={control}
              render={({
                field: { value, onChange, ...restField },
                fieldState: { error },
              }) => (
                <Autocomplete
                  {...restField}
                  options={allItems}
                  value={value}
                  onChange={(e, v) => {
                    handleItemChange(v);
                    onChange(v);
                  }}
                  disabled={formMode !== "create" && formMode !== "edit"}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value.id === value?.value.id
                  }
                  groupBy={(option) => option.value.item_type}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Item"
                      error={!!thisLineError?.item}
                      helperText={thisLineError?.item?.message}
                    />
                  )}
                />
              )}
            />
            <Controller
              name={`line_items.${index}.item_name_override`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="text"
                  label="Name Override"
                  size="small"
                  error={!!thisLineError?.item_name_override}
                  helperText={thisLineError?.item_name_override?.message}
                  disabled={
                    !(
                      (formMode === "edit" &&
                        !(fieldData.quantity_received > 0)) ||
                      formMode === "create"
                    )
                  }
                />
              )}
            />
            <Controller
              name={`line_items.${index}.uom_unit`}
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <Autocomplete
                  {...restField}
                  value={value}
                  options={filteredUOMOpts}
                  onChange={(e, v) => onChange(v)}
                  disabled={formMode !== "create" && formMode !== "edit"}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value.id === value?.value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Unit"
                      error={!!thisLineError?.uom_unit}
                      helperText={thisLineError?.uom_unit?.message}
                    />
                  )}
                />
              )}
            />
          </Stack>
        </TableCell>
        <TableCell>
          <Stack spacing={1}>
            <Controller
              name={`line_items.${index}.quantity_ordered`}
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <NumericEntryField
                  {...restField}
                  value={value}
                  onChange={onChange}
                  min={0}
                  decimalPlaces={4}
                  label="Quantity Ordered"
                  isCurrency={false}
                  disabled={
                    !(
                      (formMode === "edit" &&
                        !(fieldData.quantity_received > 0)) ||
                      formMode === "create"
                    )
                  }
                  error={!!thisLineError?.quantity_ordered}
                  helperText={thisLineError?.quantity_ordered?.message}
                  textFieldProps={{ size: "small" }}
                />
              )}
            />
            {!(formMode === "create") && (
              <Controller
                name={`line_items.${index}.quantity_received`}
                control={control}
                render={({ field: { value, onChange, ...restField } }) => (
                  <NumericEntryField
                    {...restField}
                    value={value}
                    onChange={onChange}
                    min={0}
                    decimalPlaces={4}
                    label="Received"
                    isCurrency={false}
                    disabled={true}
                    endAdornment={
                      <InputAdornment position="end">
                        <Tooltip
                          title={
                            fieldData.line_completed
                              ? "Line Complete"
                              : "Line Pending Receipt"
                          }
                        >
                          {fieldData.line_completed ? <CheckIcon /> : <></>}
                        </Tooltip>
                      </InputAdornment>
                    }
                    error={!!thisLineError?.quantity_received}
                    helperText={thisLineError?.quantity_received?.message}
                    textFieldProps={{ size: "small" }}
                  />
                )}
              />
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Controller
            name={`line_items.${index}.unit_cost`}
            control={control}
            render={({ field: { value, onChange, ...restField } }) => (
              <NumericEntryField
                {...restField}
                value={value}
                onChange={onChange}
                min={0}
                decimalPlaces={4}
                label="Unit Cost"
                isCurrency={true}
                disabled={
                  !(
                    (formMode === "edit" &&
                      !(fieldData.quantity_received > 0)) ||
                    formMode === "create"
                  )
                }
                error={!!thisLineError?.unit_cost}
                helperText={thisLineError?.unit_cost?.message}
                textFieldProps={{ size: "small" }}
                endAdornment={
                  <Controller
                    name={`line_items.${index}.is_taxed`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Tooltip title="Taxed?">
                        <IconButton
                          onClick={() => field.onChange(!field.value)}
                          edge="end"
                          size="small"
                          sx={{ ml: "auto" }}
                          disabled={!["edit", "create"].includes(formMode)}
                        >
                          <Paid color={field.value ? "success" : "disabled"} />
                        </IconButton>
                      </Tooltip>
                    )}
                  />
                }
              />
            )}
          />
        </TableCell>
        <TableCell>
          <Stack direction="column" spacing={1}>
            <Controller
              name={`line_items.${index}.revision_ordered`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="text"
                  size="small"
                  disabled={
                    !(
                      (formMode === "edit" &&
                        !(fieldData.quantity_received > 0)) ||
                      formMode === "create"
                    )
                  }
                  label="Revision Ordered"
                  error={!!thisLineError?.revision_ordered}
                  helperText={thisLineError?.revision_ordered?.message}
                />
              )}
            />
            {isBlanket && (
              <Controller
                name={`line_items.${index}.expected_date`}
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Expected Date"
                    value={field.value ? new Date(field.value) : null}
                    onChange={(date) => field.onChange(date?.toISOString())}
                    disabled={formMode === "view" || formMode === "receive"}
                    slotProps={{
                      textField: {
                        error: !!thisLineError?.expected_date,
                        helperText: thisLineError?.expected_date?.message,
                        size: "small",
                      },
                    }}
                  />
                )}
              />
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Controller
            name={`line_items.${index}.notes`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="text"
                size="medium"
                multiline
                rows={3}
                disabled={
                  !(
                    (formMode === "edit" &&
                      !(fieldData.quantity_received > 0)) ||
                    formMode === "create"
                  )
                }
                error={!!thisLineError?.notes}
                helperText={thisLineError?.notes?.message}
              />
            )}
          />
        </TableCell>
        <TableCell>
          <Stack direction="column" spacing={0.5}>
            {formMode === "receive" &&
              fieldData.requires_receipt &&
              (fieldData.item?.value.item_type === "SERVICE" ? (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setValue(
                      `line_items.${index as number}.mark_completed`,
                      !fieldData.mark_complete,
                    )
                  }
                  size="small"
                >
                  Mark Service Complete
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleReceiveDialogOpen}
                  size="small"
                >
                  Receive Line
                </Button>
              ))}
            <Box display="flex" gap={0.5}>
              {((formMode === "edit" && !(fieldData.quantity_received > 0)) ||
                formMode === "create") && (
                <Tooltip title={isDelete ? "Cancel Deletion" : "Delete Line"}>
                  <IconButton onClick={handleRemoveLine}>
                    <DeleteIcon color={isDelete ? "error" : "disabled"} />
                  </IconButton>
                </Tooltip>
                // <Button
                //   variant="outlined"
                //   color={isDelete ? "primary" : "error"}
                //   startIcon={<DeleteIcon />}
                //   onClick={handleRemoveLine}
                //   size="small"
                // >
                //   {isDelete ? "Restore" : "Remove"}
                // </Button>
              )}
              {["edit", "create"].includes(formMode) && (
                // <Button
                //   variant="outlined"
                //   color="info"
                //   startIcon={<ContentCopy />}
                //   onClick={() => onCopy(fieldData)}
                //   size="small"
                // >
                //   Copy Line
                // </Button>
                <Tooltip title={"Copy Line"}>
                  <IconButton onClick={() => onCopy(index)}>
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Controller
              name={`line_items.${index}.requires_receipt`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={
                        !(
                          (formMode === "edit" &&
                            !(fieldData.quantity_received > 0)) ||
                          formMode === "create"
                        )
                      }
                      size="small"
                    />
                  }
                  label="Requires Receipt?"
                />
              )}
            />
          </Stack>
        </TableCell>
      </TableRow>
      {(formMode === "receive" || formMode === "view") &&
        fieldData.item?.value.item_type !== "SERVICE" &&
        fieldData.requires_receipt && (
          <TableRow>
            <TableCell sx={{ verticalAlign: "top" }}>
              <IconButton
                size="small"
                onClick={() => setPrevRecsOpen(!prevRecsOpen)}
              >
                {prevRecsOpen ? <UpIcon /> : <DownIcon />}
              </IconButton>
            </TableCell>
            <TableCell colSpan={6} align="center">
              <Stack
                direction="column"
                spacing={0.5}
                alignItems="stretch"
                width="100%"
              >
                <Typography variant="h6" textAlign="center">
                  Previous Receipts
                </Typography>
                <Collapse in={prevRecsOpen} timeout="auto" unmountOnExit>
                  <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Lot</TableCell>
                          <TableCell>Receipt</TableCell>
                          <TableCell>Release</TableCell>
                          <TableCell>Details</TableCell>
                          <TableCell width="5%"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {prevRecsFetching || prevRecsLoading ? (
                          <TableRow key="fetching-data-row">
                            <TableCell colSpan={5} align="center">
                              <Box display="flex" gap={1}>
                                <CircularProgress size={24} />
                                <Typography variant="body1">
                                  Loading Previous Receipts...
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : prevRecCount > 0 ? (
                          <>
                            {prevRecs.map((prev) => (
                              <TableRow key={prev.id}>
                                <TableCell>{prev.number}</TableCell>
                                <TableCell>
                                  <Stack direction="column" spacing={0.5}>
                                    <Typography variant="body1">
                                      <strong>{prev.rec_quantity}</strong>x{" "}
                                      <strong>{prev.unit}</strong> Rev{" "}
                                      <strong>{prev.rec_revision}</strong>
                                    </Typography>
                                    <Typography variant="body1">
                                      <strong>
                                        {prev.rec_date.toLocaleDateString(
                                          "en-US",
                                          { timeZone: "America/Chicago" },
                                        )}
                                      </strong>{" "}
                                      by <em>{prev.rec_by}</em>
                                    </Typography>
                                  </Stack>
                                  {/* <Stack direction="column" spacing={0.5}>
                                    <Box
                                      display="flex"
                                      justifyContent={"space-between"}
                                    >
                                      <Typography
                                        variant="body1"
                                        fontWeight={"bold"}
                                      >
                                        Date:
                                      </Typography>
                                      <Typography variant="body1">
                                        {prev.rec_date.toLocaleDateString(
                                          "en-US",
                                          { timeZone: "America/Chicago" },
                                        )}
                                      </Typography>
                                    </Box>
                                    <Box
                                      display="flex"
                                      justifyContent={"space-between"}
                                    >
                                      <Typography
                                        variant="body1"
                                        fontWeight={"bold"}
                                      >
                                        Revision:
                                      </Typography>
                                      <Typography variant="body1">
                                        {prev.rec_revision}
                                      </Typography>
                                    </Box>
                                    <Box
                                      display="flex"
                                      justifyContent={"space-between"}
                                    >
                                      <Typography
                                        variant="body1"
                                        fontWeight={"bold"}
                                      >
                                        Quantity:
                                      </Typography>
                                      <Typography variant="body1">
                                        {prev.rec_quantity}x {prev.unit}
                                      </Typography>
                                    </Box>
                                    <Box
                                      display="flex"
                                      justifyContent={"space-between"}
                                    >
                                      <Typography
                                        variant="body1"
                                        fontWeight={"bold"}
                                      >
                                        By:
                                      </Typography>
                                      <Typography variant="body1">
                                        {prev.rec_by}
                                      </Typography>
                                    </Box>
                                  </Stack> */}
                                </TableCell>
                                <TableCell>
                                  {prev.rel_date ? (
                                    <Stack direction="column" spacing={0.5}>
                                      <Typography variant="body1">
                                        <strong>{prev.rel_quantity}</strong>x{" "}
                                        <strong>{prev.unit}</strong> Rev{" "}
                                        <strong>{prev.rel_revision}</strong>
                                      </Typography>
                                      <Typography variant="body1">
                                        <strong>
                                          {prev.rel_date.toLocaleDateString(
                                            "en-US",
                                            { timeZone: "America/Chicago" },
                                          )}
                                        </strong>{" "}
                                        by <em>{prev.rel_by}</em>
                                      </Typography>
                                    </Stack>
                                  ) : (
                                    // <Stack direction="column" spacing={0.5}>
                                    //   <Box
                                    //     display="flex"
                                    //     justifyContent={"space-between"}
                                    //   >
                                    //     <Typography
                                    //       variant="body1"
                                    //       fontWeight={"bold"}
                                    //     >
                                    //       Date:
                                    //     </Typography>
                                    //     <Typography variant="body1">
                                    //       {prev.rel_date.toLocaleDateString(
                                    //         "en-US",
                                    //         { timeZone: "America/Chicago" },
                                    //       )}
                                    //     </Typography>
                                    //   </Box>
                                    //   <Box
                                    //     display="flex"
                                    //     justifyContent={"space-between"}
                                    //   >
                                    //     <Typography
                                    //       variant="body1"
                                    //       fontWeight={"bold"}
                                    //     >
                                    //       Revision:
                                    //     </Typography>
                                    //     <Typography variant="body1">
                                    //       {prev.rel_revision}
                                    //     </Typography>
                                    //   </Box>
                                    //   <Box
                                    //     display="flex"
                                    //     justifyContent={"space-between"}
                                    //   >
                                    //     <Typography
                                    //       variant="body1"
                                    //       fontWeight={"bold"}
                                    //     >
                                    //       Quantity:
                                    //     </Typography>
                                    //     <Typography variant="body1">
                                    //       {prev.rel_quantity}x {prev.unit}
                                    //     </Typography>
                                    //   </Box>
                                    //   <Box
                                    //     display="flex"
                                    //     justifyContent={"space-between"}
                                    //   >
                                    //     <Typography
                                    //       variant="body1"
                                    //       fontWeight={"bold"}
                                    //     >
                                    //       By:
                                    //     </Typography>
                                    //     <Typography variant="body1">
                                    //       {prev.rel_by}
                                    //     </Typography>
                                    //   </Box>
                                    // </Stack>
                                    <Typography
                                      variant="body1"
                                      fontWeight="bold"
                                    >
                                      Not Yet Released
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Stack direction="column" spacing={0.5}>
                                    <Box
                                      display="flex"
                                      justifyContent={"space-between"}
                                    >
                                      <Typography
                                        variant="body1"
                                        fontWeight={"bold"}
                                      >
                                        Batch:
                                      </Typography>
                                      <Typography variant="body1">
                                        {prev.batch}
                                      </Typography>
                                    </Box>
                                    <Box
                                      display="flex"
                                      justifyContent={"space-between"}
                                    >
                                      <Typography
                                        variant="body1"
                                        fontWeight={"bold"}
                                      >
                                        Expiration:
                                      </Typography>
                                      <Typography variant="body1">
                                        {prev.exp_date
                                          ? prev.exp_date.toLocaleDateString(
                                              "en-US",
                                              { timeZone: "America/Chicago" },
                                            )
                                          : "N/A"}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            ))}
                          </>
                        ) : (
                          <TableRow key="fetching-data-row">
                            <TableCell colSpan={3} align="center">
                              <Box display="flex" gap={1}>
                                <Typography variant="body1">
                                  No Previous Receipts Found...
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={prevRecCount}
                      page={prevRecPagination.page}
                      rowsPerPage={prevRecPagination.size}
                      onPageChange={handlePrevRecPageChange}
                      onRowsPerPageChange={handlePrevRecRowsPerPageChange}
                      rowsPerPageOptions={[25, 50, 100]}
                    />
                  </TableContainer>
                </Collapse>
              </Stack>
            </TableCell>
          </TableRow>
        )}
      {formMode === "receive" &&
        fieldData.item?.value.item_type !== "SERVICE" &&
        fieldData.requires_receipt && (
          <ReceiptDialog
            open={dialogOpen}
            control={control}
            onClose={handleReceiveDialogClose}
            index={index}
            uomOpts={filteredUOMOpts}
            orderedUnit={fieldData.uom_unit!}
            neededQuant={
              fieldData.quantity_ordered - fieldData.quantity_received
            }
          />
        )}
    </>
  );
};

export const PagePurchOrderForm: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { mode: formMode, id } = useFormParams<
    PurchOrderFormMode,
    PurchOrderFormParams
  >(PURCH_ORDER_FORM_MODES, ["id"], "view");

  useEffect(() => {
    console.log("Form Mode or ID Changed", refRenderCount.current);
    setResetForm(true);
  }, [formMode, id]);

  const [resetForm, setResetForm] = useState<boolean>(true);
  const [copyOrder, setCopyOrder] = useState<boolean>(false);

  const [printNotes, setPrintNotes] = useState<boolean>(false);

  const {
    data: thisPOData,
    isLoading: thisPOLoading,
    isFetching: thisPOFetching,
    error: thisPOError,
    refetch: thisPORefetch,
  } = useTSQPurchOrder(
    copyOrder ? location.state.id : id,
    copyOrder ? !!location.state.id : !!id,
  );

  const formDefaults: POFormData = useMemo(() => {
    const today = new Date();
    const tomorrow = (() => {
      let temp = new Date(today);
      temp.setDate(temp.getDate() + 1);
      return temp;
    })();
    return {
      number: "",
      order_date: today,
      location: null,
      is_blanket: false,
      expected_date: tomorrow,
      vendor: null,
      billing_addr: {
        attn: null,
        addressee: null,
        line_1: "",
        line_2: null,
        line_3: null,
        city: "",
        state: "",
        country: "",
        zip: "",
        phone: null,
        email: null,
      },
      is_dropship: false,
      shipping_addr: {
        attn: "Receiving",
        addressee: "Centese",
        line_1: "4156 S 52nd Street",
        line_2: null,
        line_3: null,
        city: "Omaha",
        state: "Nebraska",
        country: "United States",
        zip: "68117",
        phone: null,
        email: null,
      },
      order_terms: null,
      employee: null,
      tax_rate: 0.0,
      tax_amount: 0.0,
      ship_cost: 0.0,
      notes: null,
      line_items: [],
    };
  }, []);

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<POFormData>({
    resolver: yupResolver(purchOrderSchema) as any,
    defaultValues: formDefaults,
  });

  const [
    watchedBilling,
    watchedShipping,
    shipCost,
    taxRate,
    taxAmount,
    watchedPOLines,
    watchedBlanket,
    watchedDropShip,
  ] = watch([
    "billing_addr",
    "shipping_addr",
    "ship_cost",
    "tax_rate",
    "tax_amount",
    "line_items",
    "is_blanket",
    "is_dropship",
  ]);

  const refRenderCount = useRef<number>(0);
  const refOrigData = useRef<PurchOrderFull | null>(null);
  const [displayProcessDialog, setDisplayProcessDialog] =
    useState<boolean>(false);

  function handleProcessDialogClose(
    event: any,
    reason:
      | "backdropClick"
      | "escapeKeyDown"
      | "create"
      | "receive"
      | "update"
      | "failed",
    navTo?: "view" | "list" | null,
    navToId?: string,
  ) {
    if (reason === "backdropClick") {
      return;
    }
    setDisplayProcessDialog(false);
    if (navTo === "view" && navToId) {
      navigate(`/orders/purchase/form?mode=view&id=${navToId}`);
    } else if (navTo === "list") {
      navigate(`/orders/purchase`);
    }
  }

  // State
  const [selectedVend, setSelectedVend] = useState<VendorOption | null>(null);
  const [selectedDropVend, setSelectedDropVend] = useState<VendorOption | null>(
    CentOpt,
  );
  const [error, setError] = useState<string | null>(null);
  const [overridePOGen, setOverridePOGen] = useState<boolean>(false);
  const origPONum = useRef<string>("");

  const [taxToggle, setTaxToggle] = useState<boolean>(false);

  const createPOMutation = useTSMCreatePurchOrder();
  const postProcPOMutation = useTSMPostProcessPurchOrder();
  const updatePOMutation = useTSMUpdatePurchOrder();
  const updatePOApprovalMutation = useTSMUpdatePurchOrderApproval();
  const completePOMutation = useTSMCompletePO();
  const voidPOMutation = useTSMVoidPO();
  const receivePOMutation = useTSMReceivePurchOrder();
  const generateRecDocsMutation = useTSMGenerateRecDocs();
  const statUpdateMutation = useTSMStatusUpdatePO();

  const {
    data: itemsResponse,
    isLoading: itemsLoading,
    error: itemsError,
  } = useTSQItemList({
    limit: -1,
    active_status: "active",
    filters: [[]],
  });
  const itemOpts: ItemOption[] = useMemo(() => {
    if (itemsResponse?.items) {
      const opts = itemsResponse.items.map((val) => ({
        label: val.number,
        type: "item",
        group: val.item_type,
        value: {
          id: val.id,
          number: val.number,
          revision: val.revision,
          name: val.name,
          uomTypeId: val.uom_type.id,
          item_type: val.item_type,
          expires: val.item_expires,
          purch_uom: val.purchase_uom.id,
        },
      }));
      return opts.sort((a, b) => {
        if (a.group === b.group) {
          return a.label.localeCompare(b.label);
        } else {
          return a.group.localeCompare(b.group);
        }
      }) as ItemOption[];
    } else {
      return [];
    }
  }, [itemsResponse]);

  const {
    data: unitsResponse,
    isLoading: unitsLoading,
    error: unitsError,
  } = useTSQUOMUnitList({
    limit: -1,
    active_status: "active",
    filters: [[]],
  });
  const uomOpts: UOMOption[] = useMemo(() => {
    if (unitsResponse?.items) {
      return unitsResponse.items.map((val) => ({
        label: val.unit_symbol,
        type: "unit",
        value: {
          id: val.id,
          symbol: val.unit_symbol,
          conversion: parseFloat(val.conversion_factor),
          typeId: val.uom_type_id,
        },
      }));
    } else {
      return [];
    }
  }, [unitsResponse]);

  const {
    data: locsResponse,
    isLoading: locsLoading,
    error: locsError,
  } = useTSQLocationList({
    limit: -1,
    active_status: "active",
    filters: [[]],
  });
  const locOpts: LocationOption[] = useMemo(() => {
    if (locsResponse?.items) {
      return locsResponse.items
        .filter((loc) => {
          return loc.parent_location === null;
        })
        .map((val) => ({
          label: `${val.name}`,
          type: "location",
          value: { id: val.id },
        }));
    } else {
      return [];
    }
  }, [locsResponse]);
  const omaLoc: LocationOption | null = (() =>
    locOpts.find((opt) => opt.label === "Omaha") || null)();

  const {
    data: entityResponse,
    isLoading: entityLoading,
    error: entityError,
  } = useTSQCompanyList(
    {
      limit: -1,
      skip: -1,
      active_status: "active",
      filters: [[]],
    },
    "vendor",
    true,
  );
  const vendorOpts: VendorOption[] = useMemo(() => {
    if (entityResponse?.items) {
      return entityResponse.items.map((val) => ({
        label: `${val.code} - ${val.name}`,
        type: "vendor",
        value: {
          id: val.id,
          bill_addr: val.primary_billing_addr_id || null,
          terms: val.default_payment_terms,
        },
      }));
    } else {
      return [];
    }
  }, [entityResponse]);

  const {
    data: empsResponse,
    isLoading: empsLoading,
    error: empsError,
  } = useTSQPeopleList(
    {
      limit: -1,
      skip: -1,
      active_status: "active",
      filters: [[]],
    },
    "employee",
  );
  const empOpts: EmployeeOption[] = useMemo(() => {
    if (empsResponse?.items) {
      return empsResponse.items.map((val) => ({
        label: `${val.first_name} ${val.last_name}`,
        type: "employee",
        value: { id: val.id },
      }));
    } else {
      return [];
    }
  }, [empsResponse]);

  const baseDataLoading =
    itemsLoading || unitsLoading || entityLoading || locsLoading || empsLoading;

  const dataReady =
    !baseDataLoading &&
    itemOpts.length > 0 &&
    uomOpts.length > 0 &&
    vendorOpts.length > 0 &&
    locOpts.length > 0 &&
    empOpts.length > 0;

  const formLoading =
    locsLoading ||
    unitsLoading ||
    itemsLoading ||
    thisPOLoading ||
    entityLoading ||
    empsLoading;

  useEffect(() => {
    console.group("Main Use Effect");
    if (resetForm) {
      console.log("resetForm");
      if (!thisPOFetching) {
        console.log("!thisPOFethcing");
        if (dataReady) {
          if (thisPOData && (formMode !== "create" || copyOrder)) {
            console.log("w/ Data", {
              thisPOData: thisPOData,
              dataReady: dataReady,
              formMode: formMode,
              copyOrder: copyOrder,
            });
            const thisPOLines: POLineData[] = [];
            thisPOData.line_items.forEach((line) => {
              thisPOLines.push({
                rec_id: copyOrder ? null : line.id,
                revision_ordered: line.revision_ordered,
                item_name_override: line.item_name_override,
                expected_date: copyOrder
                  ? null
                  : line.expected_date
                    ? new Date(line.expected_date)
                    : null,
                quantity_ordered: parseFloat(line.quantity_ordered),
                quantity_received: copyOrder
                  ? 0.0
                  : parseFloat(line.quantity_received),
                unit_cost: parseFloat(line.unit_cost),
                notes: line.notes,
                item:
                  itemOpts.find((opt) => opt.value.id === line.item.id) || null,
                uom_unit:
                  uomOpts.find((opt) => opt.value.id === line.uom_unit.id) ||
                  null,
                is_delete: false,
                requires_receipt: line.requires_receipt,
                mark_complete: false,
                line_completed: copyOrder ? false : line.line_completed,
                is_taxed: line.is_taxed,
                new_recs: [],
              });
            });
            const vendOpt =
              vendorOpts.find((opt) => opt.value.id === thisPOData.vendor.id) ||
              null;
            reset({
              number: copyOrder ? "" : thisPOData.number,
              order_date: copyOrder
                ? new Date()
                : new Date(thisPOData.order_date),
              location:
                locOpts.find(
                  (opt) => opt.value.id === thisPOData.location.id,
                ) || null,
              is_blanket: thisPOData.is_blanket,
              expected_date: copyOrder
                ? null
                : thisPOData.expected_date
                  ? new Date(thisPOData.expected_date)
                  : null,
              vendor: vendOpt,
              billing_addr: thisPOData.billing_addr,
              is_dropship: thisPOData.is_dropship,
              shipping_addr: thisPOData.shipping_addr,
              order_terms:
                paymentTermOptions.find(
                  (opt) => opt.value.id === thisPOData.order_terms,
                ) || null,
              employee:
                empOpts.find(
                  (opt) => opt.value.id === thisPOData.employee.id,
                ) || null,
              tax_rate: parseFloat(thisPOData.tax_rate),
              tax_amount: parseFloat(thisPOData.tax_amount),
              ship_cost: parseFloat(thisPOData.ship_cost),
              notes: thisPOData.notes,
              line_items: thisPOLines,
            });
            setSelectedVend(vendOpt);
            setResetForm(false);
            refOrigData.current = thisPOData;
          } else {
            console.log("wOut/ Data", {
              thisPOData: thisPOData,
              dataReady: dataReady,
              formMode: formMode,
              copyOrder: copyOrder,
            });
            reset({ ...formDefaults, location: omaLoc });
            setResetForm(false);
            setSelectedVend(null);
            setSelectedDropVend(CentOpt);
          }
        }
      }
    }
    console.groupEnd();
  }, [
    dataReady,
    empOpts,
    formDefaults,
    omaLoc,
    itemOpts,
    locOpts,
    uomOpts,
    reset,
    resetForm,
    thisPOData,
    thisPOFetching,
    vendorOpts,
    formMode,
    copyOrder,
  ]);

  const [formsToGen, setFormsToGen] = useState<{
    [key: string]: {
      status: boolean;
      link: string | null;
    };
  } | null>(null);

  const [allFormsGen, setAllFormsGen] = useState<boolean>(false);

  // Dialog State
  const [currentTab, setCurrentTab] = useState<number>(0);

  const {
    fields: poLineItems,
    append: appendPOLine,
    remove: removePOLine,
  } = useFieldArray({
    control,
    name: "line_items",
  });

  const { lineOrdQuants, lineOrdCosts, lineCount } = useMemo(() => {
    const retVal: {
      lineOrdQuants: number[];
      lineOrdCosts: number[];
      lineCount: number;
    } = {
      lineOrdQuants: [],
      lineOrdCosts: [],
      lineCount: watchedPOLines.length,
    };
    watchedPOLines.forEach((line) => {
      retVal.lineOrdQuants.push(line.quantity_ordered || 0);
      retVal.lineOrdCosts.push(line.unit_cost || 0);
    });
    return retVal;
  }, [watchedPOLines]);

  const subtotal = useMemo(() => {
    var tot = 0;
    for (let i = 0; i < lineCount; i++) {
      tot += lineOrdQuants[i] * lineOrdCosts[i];
    }
    return tot;
  }, [lineOrdQuants, lineOrdCosts, lineCount]);

  const grandTotal = useMemo(() => {
    return subtotal + taxAmount + shipCost;
  }, [subtotal, taxAmount, shipCost]);

  useEffect(() => {
    refRenderCount.current += 1;
  });

  // Mode switching functions
  function switchToViewMode() {
    navigate(`/orders/purchase/form?mode=view&id=${id}`);
  }

  function switchToEditMode() {
    navigate(`/orders/purchase/form?mode=edit&id=${id}`);
  }

  function switchToReceiveMode() {
    navigate(`/orders/purchase/form?mode=receive&id=${id}`);
  }

  function switchToCreateMode() {
    navigate(`/orders/purchase/form?mode=create`);
  }

  function copyPurchaseOrder() {
    setCopyOrder(true);
    navigate(`/orders/purchase/form?mode=create`, { state: { id: id } });
  }

  function addPOLine(index: number | null) {
    if (index !== null) {
      const lineData = watchedPOLines[index];
      appendPOLine({
        ...lineData,
        rec_id: null,
        is_delete: false,
        new_recs: [],
        mark_complete: false,
        line_completed: false,
        quantity_received: 0,
      });
    } else {
      appendPOLine({
        rec_id: null,
        revision_ordered: "",
        item_name_override: null,
        expected_date: null,
        item: null,
        uom_unit: null,
        quantity_ordered: 0,
        quantity_received: 0,
        unit_cost: 0,
        notes: null,
        is_delete: false,
        requires_receipt: true,
        mark_complete: false,
        line_completed: false,
        is_taxed: false,
        new_recs: [],
      });
    }
  }

  // Form submission
  async function onSubmit(data: POFormData) {
    try {
      setDisplayProcessDialog(true);
      setError(null);
      setCopyOrder(false);
      if (formMode === "create") {
        const poPayload: PurchOrderCreate = {
          number: data.number === "" ? null : data.number,
          vendor_id: data.vendor!.value.id,
          employee_id: data.employee!.value.id,
          location_id: data.location!.value.id,
          order_terms: data.order_terms!.value.id as PaymentTermType,
          order_date: data.order_date.toISOString(),
          expected_date: data.expected_date
            ? data.expected_date.toISOString()
            : null,
          tax_rate: data.tax_rate,
          tax_amount: data.tax_amount,
          ship_cost: data.ship_cost,
          notes: data.notes,
          is_blanket: data.is_blanket,
          is_dropship: data.is_dropship,
          billing_addr: data.billing_addr,
          shipping_addr: data.shipping_addr,
          line_items: data.line_items.map((dataLine) => {
            const linePayload: POLineCreate = {
              item_id: dataLine.item!.value.id,
              revision_ordered: dataLine.revision_ordered,
              quantity_ordered: dataLine.quantity_ordered,
              unit_cost: dataLine.unit_cost,
              uom_unit_id: dataLine.uom_unit!.value.id,
              requires_receipt: dataLine.requires_receipt,
              notes: dataLine.notes,
              item_name_override:
                dataLine.item_name_override === ""
                  ? null
                  : dataLine.item_name_override,
              work_order_data: null,
              is_taxed: dataLine.is_taxed,
              expected_date: dataLine.expected_date
                ? dataLine.expected_date.toISOString()
                : null,
            };
            return linePayload;
          }),
        };
        const new_po = await createPOMutation.mutateAsync(poPayload);
        const updated = await postProcPOMutation.mutateAsync({ id: new_po.id });
        handleProcessDialogClose(null, "create", "view", new_po.id);
      } else if (formMode === "receive") {
        if (!id) {
          throw new Error("ID Required for Receiving.");
        }
        const rec_lines: POLineReceipt[] = [];
        data.line_items.forEach((poLine, poLineInd) => {
          console.log("PO Line", poLineInd, poLine);
          const linePayload: POLineReceipt = {
            line_id: poLine.rec_id!,
            lot_data: [],
            mark_line_complete: poLine.mark_complete,
          };
          poLine.new_recs.forEach((newRec, newRecInd) => {
            const lotPayload: InvLotReceipt = {
              item_id: poLine.item!.value.id,
              revision: newRec.revision_received,
              uom_id: newRec.uom_unit.value.id,
              quant: newRec.quantity,
              cost: poLine.unit_cost,
              rec_loc_id: data.location!.value.id,
              exp_date: newRec.expiration_date || undefined,
              batch_code: newRec.supp_code || undefined
            };
            linePayload.lot_data.push(lotPayload);
          });
          rec_lines.push(linePayload);
        });
        const poPayload: POReceiving = {
          rec_lines: rec_lines,
        };
        const poRecData = await receivePOMutation.mutateAsync({
          id: id,
          data: poPayload,
        });
        if (poRecData.success) {
          const newDocs: {
            [key: string]: { status: boolean; link: string | null };
          } = {};
          poRecData.lots.forEach((val, ind) => {
            newDocs[val.number] = {
              status: false,
              link: null,
            };
            return true;
          });
          setFormsToGen(newDocs);

          let offset = 0;
          const batchSize = 5;
          while (offset < poRecData.lots.length) {
            const batch = poRecData.lots.slice(offset, offset + batchSize);
            let tempObj: POReceiptResponse = {
              success: poRecData.success,
              message: poRecData.message,
              id: poRecData.id,
              number: poRecData.number,
              vendor: poRecData.vendor,
              lots: batch,
            };
            const receivingDocsData = await generateRecDocsMutation.mutateAsync(
              {
                data: tempObj,
              },
            );
            setFormsToGen((prev) => {
              if (!prev) {
                prev = {};
              }
              for (let lotNum in receivingDocsData.doc_links) {
                const link = receivingDocsData.doc_links[lotNum];
                prev[lotNum] = {
                  status: link !== "Failed to generate FRM-601-1",
                  link: link,
                };
              }
              return prev;
            });
            offset += batchSize;
          }
          setAllFormsGen(true);
        }
      } else if (formMode === "edit") {
        if (!id) {
          setError("Missing Order ID, cannot perform Update");
          return;
        }
        if (refOrigData.current === null) {
          setError(
            "Could not find reference to original data object, unable to perform update.",
          );
          return;
        }
        const updatedLines: POLineUpdate[] = [];
        const deletedLines: string[] = [];
        const newLines: POLineCreate[] = [];
        data.line_items.forEach((poLine, poLineInd) => {
          if (poLine.rec_id === null) {
            newLines.push({
              item_id: poLine.item!.value.id,
              uom_unit_id: poLine.uom_unit!.value.id,
              quantity_ordered: poLine.quantity_ordered,
              unit_cost: poLine.unit_cost,
              notes: poLine.notes,
              revision_ordered: poLine.revision_ordered,
              requires_receipt: poLine.requires_receipt,
              item_name_override: poLine.item_name_override,
              expected_date: poLine.expected_date,
              is_taxed: poLine.is_taxed,
              work_order_data: null,
            } as POLineCreate);
          } else if (poLine.is_delete) {
            deletedLines.push(poLine.rec_id!);
          } else {
            const linePayload: POLineUpdate | null = (() => {
              const origLineData =
                refOrigData.current!.line_items.find(
                  (line) => line.id === poLine.rec_id,
                ) || null;
              if (!origLineData) {
                console.log("NO ORIG LINE DATA", poLine, origLineData);
                return null;
              }
              const retVal: POLineUpdate = {
                id: poLine.rec_id!,
              };
              if (poLine.item!.value.id !== origLineData.item.id) {
                retVal["item_id"] = poLine.item!.value.id;
              }
              if (poLine.uom_unit!.value.id !== origLineData.uom_unit.id) {
                retVal["uom_unit_id"] = poLine.uom_unit!.value.id;
              }
              if (
                poLine.quantity_ordered !==
                parseFloat(origLineData.quantity_ordered)
              ) {
                retVal["quantity_ordered"] = poLine.quantity_ordered;
              }
              if (poLine.unit_cost !== parseFloat(origLineData.unit_cost)) {
                retVal["unit_cost"] = poLine.unit_cost;
              }
              if (poLine.notes !== origLineData.notes) {
                retVal["notes"] = poLine.notes || undefined;
              }
              if (poLine.revision_ordered !== origLineData.revision_ordered) {
                retVal["revision_ordered"] = poLine.revision_ordered;
              }
              if (poLine.requires_receipt !== origLineData.requires_receipt) {
                retVal["requires_receipt"] = poLine.requires_receipt;
              }
              if (
                poLine.item_name_override !== origLineData.item_name_override
              ) {
                retVal["item_name_override"] =
                  poLine.item_name_override || undefined;
              }
              if (poLine.is_taxed !== origLineData.is_taxed) {
                retVal["is_taxed"] = poLine.is_taxed;
              }
              const expDate = poLine.expected_date
                ? poLine.expected_date
                : null;
              const origDate = origLineData.expected_date
                ? new Date(origLineData.expected_date)
                : null;
              if (expDate !== origDate) {
                retVal["expected_date"] = expDate
                  ? expDate.toISOString()
                  : null;
              }
              if (Object.keys(retVal).length === 1) {
                return null;
              }
              return retVal;
            })();
            if (linePayload) {
              updatedLines.push(linePayload);
            }
          }
        });
        const poPayload: any = {};
        if (updatedLines.length > 0) {
          poPayload["line_items_to_update"] = updatedLines;
        }
        if (newLines.length > 0) {
          poPayload["line_items_to_add"] = newLines;
        }
        if (deletedLines.length > 0) {
          poPayload["line_item_ids_to_delete"] = deletedLines;
        }
        if (data.number !== refOrigData.current!.number) {
          poPayload["number"] = data.number;
        }
        if (data.vendor!.value.id !== refOrigData.current!.vendor.id) {
          poPayload["vendor_id"] = data.vendor!.value.id;
        }
        if (data.employee!.value.id !== refOrigData.current!.employee.id) {
          poPayload["employee_id"] = data.employee!.value.id;
        }
        if (data.location!.value.id !== refOrigData.current!.location.id) {
          poPayload["location_id"] = data.location!.value.id;
        }
        if (data.order_terms!.value.id !== refOrigData.current!.order_terms) {
          poPayload["order_terms"] = data.order_terms!.value.id;
        }
        const ordDate = data.order_date;
        const origOrdDate = new Date(refOrigData.current!.order_date);
        if (ordDate !== origOrdDate) {
          poPayload["order_date"] = data.order_date.toISOString();
        }
        const expDate = data.expected_date
          ? new Date(data.expected_date)
          : null;
        const origExpDate = refOrigData.current.expected_date
          ? new Date(refOrigData.current!.expected_date)
          : null;
        if (expDate !== origExpDate) {
          poPayload["expected_date"] = expDate ? expDate.toISOString() : null;
        }
        if (data.tax_rate !== parseFloat(refOrigData.current?.tax_rate)) {
          poPayload["tax_rate"] = data.tax_rate;
        }
        if (data.tax_amount !== Number(refOrigData.current?.tax_amount)) {
          poPayload["tax_amount"] = data.tax_amount;
        }
        if (data.ship_cost !== parseFloat(refOrigData.current?.ship_cost)) {
          poPayload["ship_cost"] = data.ship_cost;
        }
        if (data.notes !== refOrigData.current?.notes) {
          poPayload["notes"] = data.notes;
        }
        if (data.is_blanket !== refOrigData.current?.is_blanket) {
          poPayload["is_blanket"] = data.is_blanket;
        }
        for (let key of Object.keys(refOrigData.current!.billing_addr)) {
          if (
            data.billing_addr[key as keyof TableObject<"orderaddress">] !==
            refOrigData.current!.billing_addr[
              key as keyof TableObject<"orderaddress">
            ]
          ) {
            poPayload["billing_addr"] = data.billing_addr;
            break;
          }
        }
        for (let key of Object.keys(refOrigData.current!.shipping_addr)) {
          if (
            data.shipping_addr[key as keyof TableObject<"orderaddress">] !==
            refOrigData.current!.shipping_addr[
              key as keyof TableObject<"orderaddress">
            ]
          ) {
            poPayload["shipping_addr"] = data.shipping_addr;
            break;
          }
        }
        console.log("PO UPDATE PAYLOAD:", poPayload);
        await updatePOMutation.mutateAsync({
          id: id,
          data: poPayload as PurchOrderUpdate,
        });
        handleProcessDialogClose(null, "update", "view", id);
      }
    } catch (err: any) {
      console.error("Failed to save po:", err);
      setError(
        err.response?.data?.detail || err.message || "Failed to save po",
      );
      handleProcessDialogClose(null, "failed", null, undefined);
    }
  }

  function handleTabChange(event: React.SyntheticEvent, newValue: number) {
    setCurrentTab(newValue);
  }

  function handleCancel() {
    window.open("/orders/purchase", "_self");
  }

  function handlePrintOpen() {
    if (!refOrigData.current) {
      setError("Missing Original Data Object, cannot generate PO");
      return;
    }
    const ordDate = new Date(refOrigData.current.order_date);
    const reqDate = (() => {
      if (refOrigData.current.expected_date) {
        const tempDate = new Date(refOrigData.current.expected_date);
        return `${
          tempDate.getMonth() + 1
        }/${tempDate.getDate()}/${tempDate.getFullYear()}`;
      } else {
        if (refOrigData.current.is_blanket) {
          return "See Lines for individual Expected Dates";
        } else {
          return "TBD";
        }
      }
    })();
    const itemData: {
      number: string;
      name: string;
      revision: string;
      quantity: string;
      notes: string;
      uom: string;
      rate: string;
      total: string;
      expDate: string | null;
    }[] = [];
    refOrigData.current.line_items.forEach((poLine, lineIndex) => {
      let quant = poLine.quantity_ordered.toString();
      if (quant.split(".")[1] === "0000") {
        quant = quant.split(".")[0];
      }
      itemData.push({
        number: poLine.item.number,
        name: poLine.item_name_override || poLine.item.name,
        revision: poLine.revision_ordered,
        quantity: quant,
        notes: poLine.notes || "",
        uom: poLine.uom_unit.unit_symbol,
        rate: Number(poLine.unit_cost).toFixed(2),
        total: Number(
          parseFloat(poLine.unit_cost) * parseFloat(poLine.quantity_ordered),
        ).toFixed(2),
        expDate: poLine.expected_date
          ? new Date(poLine.expected_date).toLocaleDateString("en-US")
          : "",
      });
    });
    const data: POPrintData = {
      number: refOrigData.current.number,
      orderDate: ordDate.toLocaleDateString("en-US"),
      expectedDate: reqDate,
      isBlanket: refOrigData.current.is_blanket,
      shipMethod: "Any",
      notes: printNotes ? refOrigData.current.notes || null : null,
      pricing: {
        sub: subtotal.toFixed(2),
        taxRate: (parseFloat(refOrigData.current.tax_rate) * 100).toFixed(2),
        tax: taxAmount.toFixed(2),
        ship: Number(refOrigData.current.ship_cost).toFixed(2) || "TBD",
        total: grandTotal.toFixed(2),
      },
      terms: refOrigData.current.order_terms,
      vendor: {
        name: refOrigData.current.vendor.name,
        code: refOrigData.current.vendor.code,
        billing: {
          name: refOrigData.current.vendor.name,
          attn: refOrigData.current.billing_addr.attn || "UNKNOWN",
          addressee: refOrigData.current.billing_addr.addressee || "UNKNOWN",
          line1: refOrigData.current.billing_addr.line_1,
          line2: refOrigData.current.billing_addr.line_2 || undefined,
          line3: refOrigData.current.billing_addr.line_3 || undefined,
          city: refOrigData.current.billing_addr.city,
          state: refOrigData.current.billing_addr.state,
          country: refOrigData.current.billing_addr.country,
          zip: refOrigData.current.billing_addr.zip,
        },
      },
      isDropship: refOrigData.current.is_dropship,
      shipping: {
        attn: refOrigData.current.shipping_addr.attn || "UNKNOWN",
        addressee: refOrigData.current.shipping_addr.addressee || "UNKNOWN",
        line1: refOrigData.current.shipping_addr.line_1,
        line2: refOrigData.current.shipping_addr.line_2 || undefined,
        line3: refOrigData.current.shipping_addr.line_3 || undefined,
        city: refOrigData.current.shipping_addr.city,
        state: refOrigData.current.shipping_addr.state,
        country: refOrigData.current.shipping_addr.country,
        zip: refOrigData.current.shipping_addr.zip,
      },
      items: itemData,
    };
    const htmlData = generatePurchaseOrderHTML(data);

    const printWindow = window.open(
      "",
      "_blank",
      "width=800,height=600,scrollbars=yes,resizable=yes",
    );

    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(htmlData);
      printWindow.document.close();

      printWindow.focus();
    } else {
      alert("Please allow popups for this site to enable printing.");
    }
  }

  async function approveOrder(event: React.MouseEvent) {
    if (!id) {
      setError("Missing ID, Cannot Approve Order");
      return;
    }
    const updatePayload: PurchOrderStatusUpdate = {
      id: id,
      new_status: "APPROVED",
    };
    try {
      await updatePOApprovalMutation.mutateAsync({
        id: id,
        data: updatePayload,
      });
      thisPORefetch();
    } catch (e: any) {
      setError("There was an error updating PO approval Status");
      console.error(e);
      return;
    }
  }

  async function rejectOrder(event: React.MouseEvent) {
    if (!id) {
      setError("Missing ID, Cannot Reject Order");
      return;
    }
    const updatePayload: PurchOrderStatusUpdate = {
      id: id,
      new_status: "REJECTED",
    };
    try {
      await updatePOApprovalMutation.mutateAsync({
        id: id,
        data: updatePayload,
      });
      thisPORefetch();
    } catch (e: any) {
      setError("There was an error updating PO approval Status");
      console.error(e);
      return;
    }
  }

  function handleTaxAmountChange(value: number) {
    setValue(
      "tax_rate",
      subtotal > 0 ? Number((value / subtotal).toFixed(4)) : 0,
    );
  }

  function handleTaxRateChange(value: number) {
    setValue("tax_amount", Number((subtotal * value).toFixed(4)));
  }

  if (resetForm) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
        <Typography variant="h5">Preparing Form...</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack direction="column" spacing={1}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            variant="outlined"
          >
            Back to Purchase Orders
          </Button>
          <Typography variant="h4" component="h1">
            {formMode === "create"
              ? "Create Purchase Order"
              : formMode === "view"
                ? `View PO ${thisPOData?.number}`
                : formMode === "receive"
                  ? `Receive PO ${thisPOData?.number}`
                  : `Edit PO ${thisPOData?.number}`}
          </Typography>
          <Box ml="auto"></Box>
          {(formMode === "view" || formMode === "edit") &&
            thisPOData?.approval_status === "PENDING" && (
              <SplitButton
                id="approval-split"
                options={[
                  {
                    id: "approve-btn",
                    text: "Approve",
                    variant: "contained",
                    onClick: approveOrder,
                    startIcon: <ApproveIcon />,
                  },
                  {
                    id: "reject-btn",
                    text: "Reject",
                    variant: "contained",
                    onClick: rejectOrder,
                    startIcon: <RejectIcon />,
                  },
                ]}
                isSwitch={false}
                executeText="Approval"
              />
            )}
          {formMode === "view" && !!id && refOrigData.current && (
            <OrderStatusActions
              recordType={"purchase_order"}
              recordId={id}
              disabled={false}
              orderStatus={refOrigData.current.order_status}
              approvalStatus={refOrigData.current.approval_status}
              poStatus={refOrigData.current.receipt_status}
              mutation={statUpdateMutation}
              onStatusUpdateComplete={(response) => {
                if (response.success) {
                  window.open(
                    `/orders/purchase/form?mode=view&id=${id}`,
                    "_self",
                  );
                } else {
                  setError(response.message);
                }
              }}
            />
          )}
          {formMode === "view" && (
            <SplitButton
              id="view-split"
              options={[
                {
                  id: "create-btn",
                  text: "Create New",
                  variant: "contained",
                  onClick: switchToCreateMode,
                  startIcon: <AddIcon />,
                },
                {
                  id: "copy-btn",
                  text: "Copy Order",
                  variant: "contained",
                  onClick: copyPurchaseOrder,
                  startIcon: <ContentCopy />,
                },
                {
                  id: "edit-btn",
                  text: "Edit",
                  variant: "contained",
                  onClick: switchToEditMode,
                  startIcon: <EditIcon />,
                },
                {
                  id: "receive-btn",
                  text: "Receive",
                  variant: "contained",
                  onClick: switchToReceiveMode,
                  startIcon: <InventoryIcon />,
                },
              ]}
              isSwitch={false}
              executeText="Mode Switch"
            />
          )}
          {formMode === "view" && (
            <Button
              variant="outlined"
              onClick={handlePrintOpen}
              startIcon={<PrintIcon />}
            >
              Print
            </Button>
          )}
          {formMode === "edit" && (
            <SplitButton
              id="edit-split"
              options={[
                {
                  id: "create-btn",
                  text: "Create New",
                  variant: "contained",
                  onClick: switchToCreateMode,
                  startIcon: <AddIcon />,
                },
                {
                  id: "copy-btn",
                  text: "Copy Order",
                  variant: "contained",
                  onClick: copyPurchaseOrder,
                  startIcon: <ContentCopy />,
                },
                {
                  id: "view-btn",
                  text: "View",
                  variant: "contained",
                  onClick: switchToViewMode,
                  startIcon: <VisibilityIcon />,
                },
              ]}
              isSwitch={false}
              executeText="Mode Switch"
            />
          )}
          {user?.is_superuser && (
            <Button
              variant="outlined"
              onClick={() => {
                console.log(getValues());
              }}
            >
              DEBUG PRINT
            </Button>
          )}
        </Box>

        {/* Error Alert */}
        {(error || locsError || thisPOError || itemsError || unitsError) && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error ||
              itemsError?.message ||
              unitsError?.message ||
              thisPOError?.message ||
              locsError?.message ||
              "An unknown error occurred"}
          </Alert>
        )}

        {/* Form */}
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit(onSubmit, console.error)}>
            <Stack spacing={3} sx={{ width: "100%" }}>
              {formMode !== "create" && thisPOData && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Typography variant="h6">Order Status:</Typography>
                  <Chip
                    color={statusUtils.getPrimaryOrderStatusColor(
                      thisPOData.order_status,
                    )}
                    label={stringUtils.capFirstLetter(thisPOData.order_status)}
                    size="small"
                  />
                  <Typography variant="h6">Approval Status:</Typography>
                  <Chip
                    color={statusUtils.getApprovalStatusColor(
                      thisPOData.approval_status,
                    )}
                    label={stringUtils.capFirstLetter(
                      thisPOData.approval_status,
                    )}
                    size="small"
                  />
                  <Typography variant="h6">Receipt Status:</Typography>
                  <Chip
                    color={statusUtils.getPoReceiptStatusColor(
                      thisPOData.receipt_status,
                    )}
                    label={stringUtils.capFirstLetter(
                      thisPOData.receipt_status,
                    )}
                    size="small"
                  />
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Stack
                  sx={{ direction: "column", gap: 2, width: "45%", mr: "5%" }}
                >
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                  <Controller
                    name="number"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="PO Number"
                        fullWidth
                        size="small"
                        disabled={
                          formMode === "view" ||
                          formMode === "receive" ||
                          !overridePOGen
                        }
                        error={!!errors.number}
                        helperText={
                          errors.number?.message || overridePOGen
                            ? "PO Numbers must be unique."
                            : undefined
                        }
                        placeholder="PO Number Pending Generation"
                        slotProps={{
                          inputLabel: { shrink: true },
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip
                                  title={
                                    overridePOGen
                                      ? "Lock PO Number"
                                      : formMode === "create"
                                        ? "Override PO# Generation"
                                        : "Update PO#"
                                  }
                                >
                                  <IconButton
                                    onClick={() =>
                                      setOverridePOGen(!overridePOGen)
                                    }
                                    edge="end"
                                    size="small"
                                    disabled={
                                      formMode === "view" ||
                                      formMode === "receive"
                                    }
                                  >
                                    {overridePOGen ? (
                                      <LockIcon />
                                    ) : (
                                      <EditIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reset PO#">
                                  <IconButton
                                    onClick={() => {
                                      if (overridePOGen)
                                        setOverridePOGen(false);
                                      setValue("number", origPONum.current);
                                    }}
                                    edge="end"
                                    size="small"
                                    disabled={
                                      formMode === "view" ||
                                      formMode === "receive"
                                    }
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="location"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <Autocomplete
                        {...restField}
                        fullWidth
                        value={value}
                        options={locOpts}
                        onChange={(event, newValue) => {
                          onChange(newValue);
                        }}
                        disabled={formMode !== "create" && formMode !== "edit"}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.value.id === value?.value.id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Location"
                            error={!!errors.location}
                            helperText={errors.location?.message}
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name="employee"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <Autocomplete
                        {...restField}
                        fullWidth
                        value={value}
                        options={empOpts}
                        onChange={(event, newValue) => {
                          onChange(newValue);
                        }}
                        disabled={formMode !== "create" && formMode !== "edit"}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.value.id === value?.value.id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Employee"
                            error={!!errors.employee}
                            helperText={errors.employee?.message}
                          />
                        )}
                      />
                    )}
                  />
                  <Typography variant="h6" gutterBottom>
                    Vendor Information
                  </Typography>
                  <Controller
                    name="vendor"
                    control={control}
                    render={({ field: { value, onChange, ...restField } }) => (
                      <Autocomplete
                        {...restField}
                        fullWidth
                        value={value}
                        options={vendorOpts}
                        onChange={(event, newValue) => {
                          onChange(newValue);
                          setSelectedVend(newValue);
                        }}
                        disabled={formMode !== "create" && formMode !== "edit"}
                        getOptionLabel={(option) => option?.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          option.value.id === value?.value.id
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Vendor"
                            error={!!errors.vendor}
                            helperText={errors.vendor?.message}
                          />
                        )}
                      />
                    )}
                  />
                  <AddressField
                    control={control}
                    fieldName="billing_addr"
                    fieldLabel="Billing Address"
                    watchedAddr={watchedBilling}
                    entityId={selectedVend?.value.id || null}
                    preferredAddressId={selectedVend?.value.bill_addr || null}
                    staticOptions={[
                      {
                        label: "Custom",
                        type: "address",
                        value: {
                          id: "custom",
                          addressee: null,
                          attn: null,
                          line_1: "",
                          line_2: null,
                          line_3: null,
                          country: {
                            type: "country",
                            parent: -1,
                            name: "",
                            code: "",
                            id: -1,
                          },
                          state: {
                            type: "state",
                            parent: -1,
                            name: "",
                            code: "",
                            id: -1,
                          },
                          city: {
                            type: "city",
                            parent: -1,
                            name: "",
                            code: "",
                            id: -1,
                          },
                          zip: "",
                          phone: null,
                          email: null,
                        },
                      },
                    ]}
                    onPrefill={(addr) => {
                      console.log("On Prefill Addr Object", addr);
                      setValue("billing_addr", addr, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                    onWorldLocSelect={(type, value) => {
                      console.log(
                        "On World Loc Select Type and Value",
                        type,
                        value,
                      );
                      setValue(`billing_addr.${type}`, value, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                    disabled={formMode === "view" || formMode === "receive"}
                    ready={!resetForm}
                    errors={{
                      main: errors.billing_addr
                        ? "Contains errors to be addressed"
                        : null,
                      addressee:
                        errors.billing_addr?.addressee?.message || null,
                      attn: errors.billing_addr?.attn?.message || null,
                      line_1: errors.billing_addr?.line_1?.message || null,
                      line_2: errors.billing_addr?.line_2?.message || null,
                      line_3: errors.billing_addr?.line_3?.message || null,
                      city: errors.billing_addr?.city?.message || null,
                      state: errors.billing_addr?.state?.message || null,
                      country: errors.billing_addr?.country?.message || null,
                      zip: errors.billing_addr?.zip?.message || null,
                      phone: errors.billing_addr?.phone?.message || null,
                      email: errors.billing_addr?.email?.message || null,
                    }}
                  />
                </Stack>
                <Stack
                  sx={{ direction: "column", gap: 2, width: "45%", ml: "5%" }}
                >
                  <Typography variant="h6" gutterBottom>
                    Date and Shipping Information
                  </Typography>
                  <Controller
                    name="order_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Order Date"
                        value={field.value ? new Date(field.value) : null}
                        onChange={(date) => field.onChange(date?.toISOString())}
                        disabled={formMode === "view" || formMode === "receive"}
                        slotProps={{
                          textField: {
                            error: !!errors.order_date,
                            helperText: errors.order_date?.message,
                            size: "small",
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    name={`is_blanket`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e, c) => {
                              field.onChange(e, c);
                              if (!c) {
                                setValue("expected_date", null);
                              }
                            }}
                            disabled={!["edit", "create"].includes(formMode)}
                            size="small"
                          />
                        }
                        label="Blanket PO?"
                      />
                    )}
                  />
                  {watchedBlanket ? (
                    <Typography variant="body1">
                      Blanket POs Require Line Level Expected Date
                    </Typography>
                  ) : (
                    <Controller
                      name="expected_date"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label="Expected Date"
                          value={field.value ? new Date(field.value) : null}
                          onChange={(date) =>
                            field.onChange(date?.toISOString() || "")
                          }
                          disabled={
                            formMode === "view" || formMode === "receive"
                          }
                          slotProps={{
                            textField: {
                              error: !!errors.expected_date,
                              helperText: errors.expected_date?.message,
                              size: "small",
                            },
                          }}
                        />
                      )}
                    />
                  )}
                  <Controller
                    name={`is_dropship`}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e, c) => {
                              field.onChange(e, c);
                              if (c) {
                                setSelectedDropVend(null);
                              } else {
                                setSelectedDropVend(CentOpt);
                              }
                            }}
                            disabled={!["edit", "create"].includes(formMode)}
                            size="small"
                          />
                        }
                        label="DropShip Product?"
                      />
                    )}
                  />
                  {watchedDropShip && (
                    <Autocomplete
                      fullWidth
                      value={selectedDropVend}
                      options={[CentOpt, ...vendorOpts]}
                      onChange={(event, newValue) => {
                        setSelectedDropVend(newValue);
                      }}
                      disabled={formMode !== "create" && formMode !== "edit"}
                      getOptionLabel={(option) => option?.label || ""}
                      isOptionEqualToValue={(option, value) =>
                        option.value.id === value?.value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label="DropShip Vendor"
                        />
                      )}
                    />
                  )}
                  <AddressField
                    control={control}
                    fieldName="shipping_addr"
                    fieldLabel="Shipping Address"
                    watchedAddr={watchedShipping}
                    entityId={selectedDropVend?.value.id || null}
                    preferredAddressId={
                      selectedDropVend?.value.bill_addr || null
                    }
                    staticOptions={[
                      {
                        label: "Custom",
                        type: "address",
                        value: {
                          id: "custom",
                          addressee: null,
                          attn: null,
                          line_1: "",
                          line_2: null,
                          line_3: null,
                          country: {
                            type: "country",
                            parent: -1,
                            name: "",
                            code: "",
                            id: -1,
                          },
                          state: {
                            type: "state",
                            parent: -1,
                            name: "",
                            code: "",
                            id: -1,
                          },
                          city: {
                            type: "city",
                            parent: -1,
                            name: "",
                            code: "",
                            id: -1,
                          },
                          zip: "",
                          phone: null,
                          email: null,
                        },
                      },
                      {
                        label: "Centese",
                        type: "address",
                        value: {
                          id: "centeseaddr",
                          addressee: "Centese",
                          attn: "Receiving",
                          line_1: "4156 S 52nd Street",
                          line_2: null,
                          line_3: null,
                          country: {
                            type: "country",
                            parent: -1,
                            name: "United States",
                            code: "US",
                            id: 233,
                          },
                          state: {
                            type: "state",
                            parent: 233,
                            name: "Nebraska",
                            code: "NE",
                            id: 1408,
                          },
                          city: {
                            type: "city",
                            parent: 1408,
                            name: "Omaha",
                            code: "Omaha",
                            id: 123463,
                          },
                          zip: "68117",
                          phone: null,
                          email: null,
                        },
                      },
                    ]}
                    onPrefill={(addr) => {
                      console.log("On Prefill Shipping Addr Object", addr);
                      setValue("shipping_addr", addr, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                    onWorldLocSelect={(type, value) => {
                      console.log(
                        "On Shipping World Loc Select Type and Value",
                        type,
                        value,
                      );
                      setValue(`shipping_addr.${type}`, value, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                    disabled={formMode === "view" || formMode === "receive"}
                    ready={!resetForm}
                    errors={{
                      main: errors.shipping_addr
                        ? "Contains errors to be addressed"
                        : null,
                      addressee:
                        errors.shipping_addr?.addressee?.message || null,
                      attn: errors.shipping_addr?.attn?.message || null,
                      line_1: errors.shipping_addr?.line_1?.message || null,
                      line_2: errors.shipping_addr?.line_2?.message || null,
                      line_3: errors.shipping_addr?.line_3?.message || null,
                      city: errors.shipping_addr?.city?.message || null,
                      state: errors.shipping_addr?.state?.message || null,
                      country: errors.shipping_addr?.country?.message || null,
                      zip: errors.shipping_addr?.zip?.message || null,
                      phone: errors.shipping_addr?.phone?.message || null,
                      email: errors.shipping_addr?.email?.message || null,
                    }}
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gridTemplateRows: "auto auto auto auto",
                      gap: 1,
                      width: "100%",
                      mb: "auto",
                    }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ gridColumnEnd: "span 2", gridRow: 1 }}
                    >
                      Pricing Information
                    </Typography>
                    <Box sx={{ gridColumnEnd: "span 2", gridRow: 2 }}>
                      <Controller
                        name="order_terms"
                        control={control}
                        render={({
                          field: { value, onChange, ...restField },
                        }) => (
                          <Autocomplete
                            {...restField}
                            fullWidth
                            value={value}
                            options={paymentTermOptions}
                            onChange={(event, newValue) => {
                              onChange(newValue);
                            }}
                            disabled={
                              formMode !== "create" && formMode !== "edit"
                            }
                            getOptionLabel={(option) => option?.label || ""}
                            isOptionEqualToValue={(option, value) =>
                              option.value.id === value?.value.id
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                label="Order Terms"
                                error={!!errors.order_terms}
                                helperText={errors.order_terms?.message}
                              />
                            )}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ gridColumn: 1, gridRow: 3 }}>
                      <Controller
                        name="ship_cost"
                        control={control}
                        render={({
                          field: { value, onChange, ...restField },
                        }) => (
                          <NumericEntryField
                            value={value}
                            onChange={onChange}
                            min={0}
                            decimalPlaces={4}
                            label="Shipping Cost"
                            isCurrency={true}
                            disabled={
                              formMode === "view" || formMode === "receive"
                            }
                            error={!!errors.ship_cost}
                            helperText={errors.ship_cost?.message}
                            textFieldProps={{ size: "small", ...restField }}
                          />
                        )}
                      />
                    </Box>
                    <Stack sx={{ gridColumn: 1, gridRow: 4, gap: 1 }}>
                      {formMode !== "view" && (
                        <FormControlLabel
                          sx={{ alignSelf: "center" }}
                          control={
                            <Switch
                              size="small"
                              checked={taxToggle}
                              onChange={(e, c) => {
                                setTaxToggle(c);
                              }}
                            />
                          }
                          label={`Set tax by: ${taxToggle ? "Rate" : "Amount"}`}
                          labelPlacement="top"
                        />
                      )}
                      {(taxToggle || formMode === "view") && (
                        <Controller
                          name="tax_rate"
                          control={control}
                          render={({
                            field: { value, onChange, ...restField },
                          }) => (
                            <PercentageField
                              label="Tax Rate (%)"
                              value={value}
                              onChange={(value) => {
                                onChange(value);
                                handleTaxRateChange(value);
                              }}
                              disabled={
                                formMode === "view" || formMode === "receive"
                              }
                              error={!!errors.tax_rate}
                              helperText={errors.tax_rate?.message}
                              fullWidth
                              size="small"
                              {...restField}
                            />
                          )}
                        />
                      )}
                      {(!taxToggle || formMode === "view") && (
                        <Controller
                          name="tax_amount"
                          control={control}
                          render={({
                            field: { value, onChange, ...restField },
                          }) => (
                            <NumericEntryField
                              value={value}
                              onChange={(value) => {
                                onChange(value);
                                handleTaxAmountChange(value);
                              }}
                              isCurrency={true}
                              decimalPlaces={4}
                              min={0}
                              label="Tax Amount"
                              disabled={
                                formMode === "view" || formMode === "receive"
                              }
                              error={!!errors.tax_amount}
                              helperText={errors.tax_amount?.message}
                              textFieldProps={{ size: "small", ...restField }}
                            />
                          )}
                        />
                      )}
                    </Stack>
                    <Box
                      sx={{ gridColumn: 2, gridRow: 3, gridRowEnd: "span 2" }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gridTemplateRows: "auto auto auto auto",
                          bgcolor: "grey.200",
                          borderRadius: 2,
                          p: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ gridColumn: 1, gridRow: 1, textAlign: "start" }}
                        >
                          Subtotal
                        </Typography>
                        <NumericDisplayField
                          value={subtotal}
                          decimalPlaces={2}
                          isCurrency={true}
                          typographyProps={{
                            variant: "h6",
                            sx: {
                              gridColumn: 2,
                              gridRow: 1,
                              textAlign: "end",
                            },
                          }}
                        />
                        <NumericDisplayField
                          value={taxRate * 100}
                          decimalPlaces={2}
                          isCurrency={false}
                          typographyProps={{
                            variant: "h6",
                            sx: {
                              gridColumn: 1,
                              gridRow: 2,
                              textAlign: "start",
                            },
                          }}
                          startAdornment="Tax ("
                          endAdornment="%)"
                        />
                        {/* <Typography
                        variant="h6"
                        sx={{ gridColumn: 1, gridRow: 2, textAlign: "start" }}
                      >
                        {`Tax (${(isNaN(taxRate) ? 0 : taxRate) * 100}%)`}
                      </Typography> */}
                        <NumericDisplayField
                          value={taxAmount}
                          decimalPlaces={2}
                          isCurrency={true}
                          typographyProps={{
                            variant: "h6",
                            sx: {
                              gridColumn: 2,
                              gridRow: 2,
                              textAlign: "end",
                            },
                          }}
                        />
                        <Typography
                          variant="h6"
                          sx={{ gridColumn: 1, gridRow: 3, textAlign: "start" }}
                        >
                          S&H
                        </Typography>
                        <NumericDisplayField
                          value={shipCost}
                          decimalPlaces={2}
                          isCurrency={true}
                          typographyProps={{
                            variant: "h6",
                            sx: {
                              gridColumn: 2,
                              gridRow: 3,
                              textAlign: "end",
                            },
                          }}
                        />
                        <Typography
                          variant="h6"
                          sx={{ gridColumn: 1, gridRow: 4, textAlign: "start" }}
                        >
                          Total
                        </Typography>
                        <NumericDisplayField
                          value={grandTotal}
                          decimalPlaces={2}
                          isCurrency={true}
                          typographyProps={{
                            variant: "h6",
                            sx: {
                              gridColumn: 2,
                              gridRow: 4,
                              textAlign: "end",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </Box>

              {formMode === "view" && (
                <Box display="flex" sx={{ mr: "auto" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={printNotes}
                        onChange={() => setPrintNotes((prev) => !prev)}
                        size="small"
                      />
                    }
                    label="Print Notes?"
                    labelPlacement="top"
                  />
                </Box>
              )}

              <Controller
                name="notes"
                control={control}
                render={({ field: { value, onChange, ...restField } }) => (
                  <TextField
                    value={value}
                    onChange={onChange}
                    {...restField}
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes"
                    disabled={formMode === "view" || formMode === "receive"}
                    slotProps={{
                      inputLabel: {
                        shrink: !!value,
                      },
                    }}
                  />
                )}
              />

              <Divider />

              {formMode === "view" && (
                <Tabs value={currentTab} onChange={handleTabChange}>
                  <Tab label="Items" />
                  <Tab label="Audit Logs" />
                </Tabs>
              )}
              <Box display={currentTab === 0 ? "block" : "none"}>
                {errors.line_items?.message ===
                  "At least one line item is required." && (
                  <Alert severity="error">
                    At least one PO Line Item is Required.
                  </Alert>
                )}
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: "8%" }}>Row #</TableCell>
                        <TableCell sx={{ width: "15%" }}>Item</TableCell>
                        <TableCell sx={{ width: "15%" }}>Quantity</TableCell>
                        <TableCell sx={{ width: "15%" }}>Unit Cost</TableCell>
                        <TableCell sx={{ width: "15%" }}>Details</TableCell>
                        <TableCell sx={{ width: "20%" }}>Notes</TableCell>
                        <TableCell sx={{ width: "12%" }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {poLineItems.map((field, index) => (
                        <POLine
                          key={field.id}
                          fieldData={field}
                          index={index}
                          control={control}
                          setValue={setValue}
                          remove={removePOLine}
                          allItems={itemOpts}
                          allUoms={uomOpts}
                          formMode={formMode}
                          thisLineError={
                            errors.line_items
                              ? errors.line_items[index]
                              : undefined
                          }
                          onCopy={addPOLine}
                          isBlanket={watchedBlanket}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <Box display={currentTab === 1 ? "block" : "none"}>
                <AuditHistoryViewer
                  viewerType="record"
                  tableName="purchase_orders"
                  recordId={id || ""}
                  fetch={!formLoading && formMode === "view"}
                />
              </Box>
              {!(formMode === "receive") && !(formMode === "view") && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addPOLine(null)}
                  >
                    Add Line
                  </Button>
                </Box>
              )}

              {/* Form Actions */}
              {!(formMode === "view") && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    pt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? "Saving..."
                      : formMode === "create"
                        ? "Create Purchase Order"
                        : formMode === "receive"
                          ? "Receive Purchase Order"
                          : "Update Purchase Order"}
                  </Button>
                </Box>
              )}
              <Dialog
                open={displayProcessDialog}
                onClose={(e, r) => {
                  if (formMode !== "receive") {
                    return;
                  } else {
                    handleProcessDialogClose(e, r);
                  }
                }}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
              >
                <DialogContent>
                  {formMode === "create" ? (
                    <Stack direction="column" gap={3}>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">Order</Typography>
                        {createPOMutation.isPending ? (
                          <CircularProgress />
                        ) : createPOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">Box/Slack</Typography>
                        {createPOMutation.isPending ||
                        postProcPOMutation.isPending ? (
                          <CircularProgress />
                        ) : postProcPOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box>
                      {createPOMutation.isPending ||
                      postProcPOMutation.isPending ? (
                        <Typography variant="h5">
                          Form will navigate to "View" mode once requests are
                          completed...
                        </Typography>
                      ) : createPOMutation.isSuccess &&
                        postProcPOMutation.isSuccess ? (
                        <Typography variant="h5">
                          Requests completed, returning to "View" mode...
                        </Typography>
                      ) : (
                        <Typography variant="h5">
                          Requests failed, returning to "Create" mode, try
                          reqest again and notify IT if failures persist...
                        </Typography>
                      )}
                    </Stack>
                  ) : formMode === "receive" ? (
                    <Stack direction="column" gap={3}>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">Receiving Lots...</Typography>
                        {receivePOMutation.isPending ? (
                          <CircularProgress />
                        ) : receivePOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box>
                      {receivePOMutation.isError ? (
                        <Typography variant="h5" color="error">
                          ERROR RECEIVING PO
                        </Typography>
                      ) : !formsToGen ? (
                        <Typography variant="h5">
                          Waiting for lots to be received...
                        </Typography>
                      ) : !!formsToGen ? (
                        <Stack direction="column" gap={1}>
                          {Object.entries(formsToGen).map((val, index) => {
                            return (
                              <Box
                                display="flex"
                                gap={2}
                                justifyContent="space-evenly"
                              >
                                <Typography variant="body1">
                                  {val[0]}
                                </Typography>
                                {val[1].link === null ? (
                                  <CircularProgress />
                                ) : val[1].status ? (
                                  <IconButton
                                    onClick={() => {
                                      window.open(
                                        val[1].link || undefined,
                                        "_blank",
                                        "noopener,noreferrer",
                                      );
                                    }}
                                  >
                                    <FileIcon color="primary" />
                                  </IconButton>
                                ) : (
                                  <CancelIcon color="error" />
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <></>
                      )}
                      {!receivePOMutation.isPending && allFormsGen && !!id && (
                        <Button
                          variant="contained"
                          onClick={() => {
                            handleProcessDialogClose(
                              null,
                              "receive",
                              receivePOMutation.isSuccess ? "view" : null,
                              id,
                            );
                          }}
                        >
                          {`Close Processing Dialog`}
                        </Button>
                      )}
                    </Stack>
                  ) : (
                    <Stack direction="column" gap={3}>
                      <Box display="flex" gap={2} justifyContent="space-evenly">
                        <Typography variant="h5">Updating Order</Typography>
                        {updatePOMutation.isPending ? (
                          <CircularProgress />
                        ) : updatePOMutation.isSuccess ? (
                          <CheckIcon color="primary" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </Box>
                      {updatePOMutation.isPending ? (
                        <Typography variant="h5">
                          Form will navigate to "View" mode once requests are
                          completed...
                        </Typography>
                      ) : (
                        <Typography variant="h5">
                          Requests completed, returning to "View" mode...
                        </Typography>
                      )}
                    </Stack>
                  )}
                </DialogContent>
              </Dialog>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </LocalizationProvider>
  );
};
